/*
 * NkfAecEffect — jitsi-meet `JitsiLocalTrack.setEffect()` adapter for the
 * browser-side NKF-AEC engine.
 *
 * Drops into the same slot that lib-jitsi-meet's NoiseSuppressionEffect
 * occupies, but unlike NS this effect needs a *far-end* reference (= the
 * mixed remote audio being played to the user).  Because that reference
 * can't come from the local mic stream alone, the consumer must call
 * setFarEndSource(node) with an AudioNode carrying the remote-mix before
 * startEffect() runs.
 *
 *   import { NkfAecEffect } from './NkfAecEffect.js';
 *   import { calibrate }    from './calibrator.js';
 *
 *   // 1. (recommended) measure the speaker-to-mic round-trip first.
 *   const { delaySamples } = await calibrate({
 *       voiceUrl:   '/libs/nkf-aec/calibration_voice.wav',
 *       workletUrl: '/libs/nkf-aec/sync-aec-worklet.js',
 *   });
 *
 *   const effect = new NkfAecEffect({
 *       modelUrl:    '/libs/nkf-aec/models/nkf_kgnet.onnx',
 *       workletPath: '/libs/nkf-aec/sync-aec-worklet.js',
 *       farDelaySamples: delaySamples,
 *   });
 *   await effect.load();
 *   effect.setFarEndSource(remoteMixNode);
 *   await localAudioTrack.setEffect(effect);
 *
 * NoiseSuppressionEffect contract (verified against lib-jitsi-meet):
 *   - constructor()
 *   - isEnabled(): boolean
 *   - startEffect(MediaStream) -> MediaStream     (called by setEffect)
 *   - stopEffect(): void
 */

import { DelayTracker } from './DelayTracker.js';
import { gccPhat } from './calibrator.js';
import { NkfOnnxEngine } from './nkf-engine.js';

// Peak-amplitude clip detection.  AEC assumes a linear path; saturated mic
// or far signals violate that and nothing the model can do will recover.
// Fire onClippingDetected (and log "音がでかすぎ") when either side hits
// near-full-scale Int16, throttled to once per CLIP_THROTTLE_MS.
const CLIP_THRESHOLD   = 32500;     // ≈ -0.07 dBFS
const CLIP_THROTTLE_MS = 2000;

// Backpressure limit: chunks may be this many deep in the worker before we stop
// waiting and let one through as raw mic (see _sendChunkToWorker).
//
// ★ Module scope on purpose. EMIT_QUEUE_CAP lives inside the emit worklet's
//   source STRING, which is evaluated in the AudioWorkletGlobalScope — it is
//   not visible here, and reaching for it from a class method throws
//   (ReferenceError) on every chunk, which silently kills the whole AEC output.
//   The queue capacity is read from the worklet's own report instead.
const MAX_PENDING = 4;

// AudioContext ごとに、emit ワークレットの module を入れ終えたか覚えておく。
//
// ★ モジュールスコープに置くのは意図的。ワークレットの登録状態は AudioContext
//   （＝その AudioWorkletGlobalScope）に紐づくのであって、エフェクトのインスタンス
//   には紐づかない。this._loaded だけでは 2 個目のインスタンスを止められない。
//   AudioContext が捨てられれば項目ごと消えるよう WeakSet を使う。
const _emitModuleAdded = new WeakSet();

// 調査用: AudioContext ごとの通し番号。同じ AC を共有しているのか、別物なのかで
// ワークレット登録も共有エンジンの当たり方も変わるので、ログで見分けられるようにする。
// 送信側の AEC は同時に1本だけ。
//
// 推論ワーカーはエンジンを1個しか持たない(_engine/_micRem/_farRem が単一)ので、
// 2本が同時に流し込むと適応フィルタが2系統の信号に交互に適応し、どちらにも
// 収束しないまま引き算をする ＝ 送信音が壊れる。farDelaySamples も奪い合う。
//
// エフェクトはローカルトラック生成のたびに作られる(loadEffects)一方、古い方の
// 停止は lib-jitsi-meet がトラックを破棄したときにしか走らない。マイクOFFで
// 入室してから有効化すると、古いトラックが残るため停止が走らず、2本が並走した。
// 実測で確認済み(2026-08-27)。その経路を塞げない以上、ここで不変条件を守る。
let _activeEffect = null;

const _acIds = new WeakMap();
let _acSeq = 0;

function _dbgAcId(ac) {
    if (!ac) return '—';
    if (!_acIds.has(ac)) _acIds.set(ac, ++_acSeq);

    return 'ac#' + _acIds.get(ac) + '(' + ac.sampleRate + 'Hz,' + ac.state + ')';
}

// ── Forced bypass, for testing the "扇風機" artifact ───────────────────────
// When the worker can't keep up, the chunk is passed through as RAW MIC instead
// of cleaned audio. Audio stays continuous — no hole — but processed and raw
// chunks then alternate every 32 ms, and they differ in level and timbre. The
// suspicion is that this alternation is what people describe as a warble rather
// than a dropout.
//
// Overload is hard to summon on demand, so this forces the same mixing directly:
// set a percentage and that share of chunks is bypassed regardless of load. If
// the result sounds like what users report, the mechanism is confirmed —
// the same way the jank button confirmed the dropout mechanism.
//
// 0 = off (normal). 100 = every chunk raw (equivalent to AEC off; useful as the
// reference point at the other end).
let _forceBypassPct = 0;

/**
 * Sets the share of chunks to bypass on purpose.
 *
 * @param {number} pct - 0..100.
 * @returns {void}
 */
export function setNkfForceBypassPct(pct) {
    _forceBypassPct = Math.max(0, Math.min(100, Number(pct) || 0));
    console.warn(`[nkf][force-bypass] ${_forceBypassPct}% — `
        + (_forceBypassPct === 0 ? 'off (normal processing)'
            : _forceBypassPct === 100 ? 'every chunk raw mic (AEC effectively off)'
                : 'processed and raw chunks alternating'));
}

/**
 * Current forced-bypass share.
 *
 * @returns {number}
 */
export function getNkfForceBypassPct() {
    return _forceBypassPct;
}

// ── Media stats for the debug panel ────────────────────────────────────────
// The alive log resets its counters every 5 s, so anything polling them shows a
// sawtooth. Keep a real rolling window of the dropout reports (they arrive ~4x
// per second) plus the last completed 5 s snapshot, and let the panel read both.
const MEDIA_WINDOW_MS = 5000;
let _mediaUrRing = [];
let _mediaLast = null;

/**
 * Transmitted-audio health over a rolling window.
 *
 * 空 (urMs)  = the emit queue ran dry and silence went out.
 * 溢 (trimMs) = the queue overflowed and cleaned audio was dropped.
 * Both are real holes the far side hears; together they are the dropout rate.
 *
 * @returns {Object}
 */
export function getNkfMediaStats() {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const cutoff = now - MEDIA_WINDOW_MS;

    _mediaUrRing = _mediaUrRing.filter(r => r.at >= cutoff);

    let urMs = 0, trimMs = 0, events = 0;

    for (const r of _mediaUrRing) {
        urMs += r.urMs;
        trimMs += r.trimMs;
        events += r.events;
    }

    return {
        windowMs: MEDIA_WINDOW_MS,
        urMs,
        trimMs,
        events,
        lossPct: (urMs + trimMs) / MEDIA_WINDOW_MS * 100,
        eventsPerSec: events / (MEDIA_WINDOW_MS / 1000),
        last: _mediaLast
    };
}

// ── Shared ONNX inference Worker (page-wide singleton) ──────────────────────
// A camera on/off fires a browser `devicechange`, which lib-jitsi-meet turns
// into a mic-track "ended" → the app re-runs getUserMedia and builds a BRAND
// NEW NkfAecEffect.  If each effect spawned its own Worker we'd pay ~1.5-2 s of
// ORT+model load every toggle (surfaces as an AEC-bypass burst = echo leaks +
// choppy send) AND leak the old Worker (never terminated — stopEffect keeps it
// "warm").  Instead the Worker lives at module scope and is reused across
// effect (re)creations, so the model load happens once and the adaptive
// filter's convergence survives the track swap.  Results are routed to
// the effect that sent each chunk, addressed by id (_sharedWorkerEffects).
let _sharedWorker = null;
let _sharedWorkerModelUrl = null;  // guard: only reuse for the same model

// Results are addressed to the effect that SENT the chunk, looked up by id.
//
// The previous design routed every result to a single "current owner", which
// load() claimed at construction — before the effect had an audio graph. Since
// a new effect is built for every local track (changing the mic on the prejoin
// screen does exactly that), the owner could end up being an effect that never
// received a track, while the effect actually wired to the outgoing stream got
// nothing back. Its _emitCleaned then never ran: no audio out, no cancellation.
// Switching the mic "fixed" it only because the rebuild reclaimed ownership.
//
// Addressing by id removes the shared mutable owner entirely, so a result can
// only ever reach the effect that produced its input.
const _sharedWorkerEffects = new Map();   // effectId → NkfAecEffect
let _effectSeq = 0;

// Pending resolver for a re-init (see resetSharedAecEngine). The Worker answers
// an 'init' with 'ready', which the result dispatcher forwards here.
let _sharedWorkerReinit = null;

/**
 * Recovers the sending effect's id from a chunk id ("<effectId>:<seq>").
 *
 * The Worker never inspects the id, it just echoes it, so carrying the address
 * here keeps the Worker (deployed separately under public/libs/, outside the
 * webpack bundle) completely unchanged.
 *
 * @param {string|number} id - The id returned by the Worker.
 * @returns {number} The effect id, or 0 when it cannot be read.
 */
function _ownerIdOf(id) {
    if (typeof id !== 'string') return 0;
    const cut = id.indexOf(':');

    return cut > 0 ? (parseInt(id.slice(0, cut), 10) || 0) : 0;
}

/**
 * Throws away the shared engine's adaptive state and starts it clean.
 *
 * Call this when the MICROPHONE changes. The filter that the Worker carries
 * across effect rebuilds is tuned to one specific speaker→mic path; keeping it
 * is what makes a camera toggle cheap, but on a different microphone that same
 * state describes the wrong acoustics and cancellation cannot recover from it
 * (the echo simply never lines up again).
 *
 * The Worker has no 'reset' message, but 'init' rebuilds the engine — every
 * Kalman and GRU buffer is reallocated at zero — so no Worker-side change is
 * needed. That matters: the Worker ships as a separate file under public/libs/,
 * outside the webpack bundle, so editing it would risk shipping a mismatch.
 *
 * No-op when no shared Worker exists yet (nothing to reset).
 *
 * @param {string} modelUrl - Model to load into the fresh engine.
 * @param {number} [farDelaySamples] - Delay to start from (default 0).
 * @returns {Promise<void>} Resolves once the engine reports ready.
 */
export function resetSharedAecEngine(modelUrl, farDelaySamples = 0) {
    if (!_sharedWorker || _sharedWorkerModelUrl !== modelUrl) {
        return Promise.resolve();
    }

    return new Promise(resolve => {
        // Don't leave callers hanging if the Worker never answers; the engine
        // stays as it was, which is no worse than not having tried.
        const timer = setTimeout(() => {
            if (_sharedWorkerReinit) {
                _sharedWorkerReinit = null;
                console.warn('[nkf] engine reset timed out — continuing with the existing filter');
                resolve();
            }
        }, 8000);

        _sharedWorkerReinit = () => {
            clearTimeout(timer);
            console.info('[nkf] engine reset (mic changed) — adaptive filter cleared');
            resolve();
        };
        try {
            _sharedWorker.postMessage({ type: 'init', modelUrl, farDelaySamples });
        } catch (e) {
            clearTimeout(timer);
            _sharedWorkerReinit = null;
            console.warn('[nkf] engine reset failed to post:', e);
            resolve();
        }
    });
}

export class NkfAecEffect {
    /**
     * @param {object} opts
     * @param {string} [opts.modelUrl]         URL of nkf_kgnet.onnx.
     * @param {string} [opts.workletPath]      URL of sync-aec-worklet.js.
     * @param {number} [opts.farDelaySamples]  Round-trip delay (samples @ 16 kHz).
     *        Pass the value returned by `calibrate()`.  Defaults to 0 which
     *        almost certainly under-aligns NKF's L=4 (= 64 ms) tap window;
     *        calibration is strongly recommended.
     */
    constructor(opts = {}) {
        this.modelUrl    = opts.modelUrl    ?? '/libs/nkf-aec/models/nkf_kgnet.onnx';
        this.workletPath = opts.workletPath ?? '/libs/nkf-aec/sync-aec-worklet.js';
        this.workerPath  = opts.workerPath  ?? '/libs/nkf-aec/nkf-aec-worker.js';
        this.farDelaySamples = (opts.farDelaySamples | 0) || 0;

        /** Passive continuous delay tracking (replaces one-shot calibration).
         *  Default on; set opts.autoDelayTrack === false to disable. */
        this._autoDelayTrack = opts.autoDelayTrack !== false;
        /** @type {DelayTracker|null} */
        this._delayTracker = null;

        /** Interval id for the outputLatency watcher that rearms the tracker. */
        this._outLatPollId = null;
        this._lastOutLatMs = 0;

        /** Notified with every far-delay change (tracker + calibration) so the
         *  factory can persist the converged value at module scope. That lets a
         *  rebuilt effect (camera toggle → mic re-gUM) START already aligned
         *  instead of dumping to 0 and re-converging — the main reason echo
         *  cancellation "collapsed" on video ON/OFF. @type {((n:number)=>void)|null} */
        this._onDelayChange = opts.onDelayChange || null;

        /** Notified once, when echo cancellation is judged to be working.
         *  OBSERVATION ONLY — nothing in the audio path reads this. It exists so
         *  a real meeting can tell us whether the "not ready" window lines up
         *  with the echo loops users hit right after joining.
         *  @type {((reason:string)=>void)|null} */
        this._onReady = opts.onReady || null;

        /** Notified when the delay is known but the mic is still held silent for
         *  the grace period. Separate from _onReady so the UI can tell "we have
         *  no idea yet" apart from "we know, we are just not transmitting yet" —
         *  both are silent to the far side, and the user deserves to know which.
         *  @type {((reason:string)=>void)|null} */
        this._onSettling = opts.onSettling || null;

        /** True once judged working. One-shot: never goes back to false within a
         *  session. A delay that drifts later re-converges on its own, and
         *  flapping this flag would only add noise to the diagnosis. */
        this._aecReady = false;

        /** Safety-valve timer (see _armReadyWatch). Lives outside the audio
         *  callback so it still fires when no audio is being produced. */
        this._readyTimer = null;

        /** Chunk count when the watch was armed, to tell "converging slowly"
         *  apart from "not processing at all". */
        this._readyChunksAtArm = 0;

        /** Hold the TRANSMITTED audio (send silence) until cancellation is
         *  judged working, plus a grace period. Config: nkfAec.holdUntilReady.
         *
         *  Armed only on a fresh start. A rebuild that reuses a converged delay
         *  is already aligned, so muting again would cut the user off every time
         *  they toggle the camera — see createNkfAecEffect. */
        this._holdUntilReady = opts.holdUntilReady === true;

        /** Extra margin between "ready" and letting audio out, so a delay that
         *  has only just converged has time to settle.
         *  Config: nkfAec.readyGraceMs. @type {number} */
        this._readyGraceMs = Number.isFinite(opts.readyGraceMs) ? opts.readyGraceMs : 3000;

        /** True while the transmitted stream is being replaced with silence. */
        this._txHold = false;

        // ── Pipeline health, per 5 s window ─────────────────────────────────
        // The dropout counters say audio was LOST. These say how close the
        // pipeline is to losing it, and where the time is going when it does:
        // the cleaned audio crosses render thread → main → worker → main →
        // render, and until now only the last hop was measured.

        /** Deepest backlog of chunks awaiting inference. Bypass fires at
         *  MAX_PENDING; sitting one below it is invisible without this. */
        this._pendingMax = 0;

        /** Emit-queue depth reported by the worklet (capacity 3 ≈ 96 ms). */
        this._qMinWindow = 99;
        this._qAvgSum = 0;
        this._qAvgN = 0;

        /** Emit-queue capacity, as reported by the worklet itself. Reading it
         *  from the report rather than duplicating the constant keeps the two
         *  from drifting apart. */
        this._qCap = 3;

        /** ONNX inference time. The worker has always measured and sent this;
         *  nothing read it. */
        this._onnxSum = 0;
        this._onnxMax = 0;
        this._onnxN = 0;

        /** Chunk round trip: postMessage out → result back. Covers both main
         *  thread hops plus the worker, so a gap between this and onnxMs is
         *  time lost to scheduling rather than to inference. */
        this._rttSum = 0;
        this._rttMax = 0;
        this._rttN = 0;

        /** Send timestamps, keyed by chunk id. Bounded by the in-flight count. */
        this._sentAt = new Map();

        /** Grace timer between readiness and actually transmitting. */
        this._txHoldTimer = null;

        /** Notified when the transmitted audio starts, or stops, losing a
         *  meaningful share of itself. Fires on the transition only, not every
         *  window, so the UI can show one message rather than a stream of them.
         *  @type {((on:boolean, pct:number)=>void)|null} */
        this._onDropout = opts.onDropout || null;

        /** Share of the window that has to go missing before we say so, and the
         *  lower share it has to come back under before we take it back.
         *  Two thresholds, not one: a single one would flip the message on and
         *  off every few seconds for anyone sitting right at it. */
        this._dropoutWarnPct = Number.isFinite(opts.dropoutWarnPct) ? opts.dropoutWarnPct : 2;
        this._dropoutClearPct = Number.isFinite(opts.dropoutClearPct) ? opts.dropoutClearPct : 0.5;

        /** True while the warning stands. */
        this._dropoutOn = false;

        /** Consecutive windows over / under the thresholds. One bad window is
         *  a blip — a hiccup nobody heard — so we want two in a row either way. */
        this._dropoutOverRuns = 0;
        this._dropoutUnderRuns = 0;

        /** Ambient mic power measured before the far end first speaks. ERLE is
         *  only meaningful when the echo actually rises above this. */
        this._ambientMicPow = 0;
        this._ambientN = 0;

        /** User AEC ON/OFF toggle. true = OFF (pass raw mic through). */
        this._userBypass = false;

        /** Post-AEC makeup gain (slow AGC). Raises the cleaned level without the
         *  fast dynamics that would break the linear AEC. */
        this._agcEnabled = opts.makeupGain !== false;

        /** 実行時の ON/OFF（ツールバーから切り替える）。
         *
         *  _agcEnabled とは役割が違う。_agcEnabled は「ゲインとリミッターの
         *  ノードをグラフに作るか」で、構成で決まり後から変えられない。こちらは
         *  作られたゲインを動かすかどうかで、通話中に切り替わる。分けておかないと、
         *  OFF で作ってしまったグラフには戻すノードが無い。 */
        this._agcActive = opts.makeupGainActive !== false;

        /** 送信メーターを出したいか。エフェクトを作り直しても指定が残るよう、
         *  createNkfAecEffect が持っている値をここへ引き継ぐ。 */
        this._meterWanted = opts.txMeter === true;
        this._agcLevel = 0;          // smoothed output RMS (0..1)
        this._makeupGainNode = null; // Web Audio GainNode (smooth level)
        this._limiterNode = null;    // Web Audio DynamicsCompressor (peak limiter)

        /** Optional RNNoise (noise-suppression) stage, spliced AFTER the AEC
         *  engine (_emitNode) but BEFORE the makeup gain, so the AGC amplifies
         *  the DENOISED signal — WebRTC APM order: AEC → NS → AGC → limiter.
         *  Default OFF; toggled live via setNoiseSuppression(). */
        this._nsEnabled = opts.noiseSuppression === true;
        this._nsWorkletPath = opts.nsWorkletPath ?? '/libs/noise-suppressor-worklet.min.js';
        this._nsNode = null;          // AudioWorkletNode('NoiseSuppressorWorklet')
        this._nsModulePromise = null; // addModule() promise (loaded once per _ac)

        /** @type {NkfOnnxEngine|null} — main-thread fallback when the Worker
         *  fails to start.  When _worker is set, engine stays null. */
        this.engine = null;

        /** @type {Worker|null} — off-main-thread ONNX inference. */
        this._worker = null;
        this._workerPending = 0;   // chunks sent, results not yet back
        this._chunkId = 0;

        /** Identifies this effect in the shared Worker's traffic. Rides along
         *  in the chunk id so every result can be returned to its sender. */
        this._effectId = ++_effectSeq;
        console.info(`[nkf-dbg] effect#${this._effectId} 生成 (この時点の生存数=${_sharedWorkerEffects.size})`);

        // Far-end accepts either an AudioNode (legacy demo path; bridged to
        // a MediaStream internally) or a MediaStream (preferred for jitsi
        // integration where the remote mix lives in a separate AC).
        /** @type {MediaStream|null} */
        this.farEndStream = null;
        /** @type {AudioNode|null} */
        this.farEndSource = null;
        /** @type {MediaStreamAudioDestinationNode|null} */
        this._farEndBridgeDest = null;

        /** Optional callback invoked with each batch of cleaned 16 kHz Int16
         *  samples produced by NKF.  Useful for piping into a server-side
         *  STT or recording sink.  Receives a fresh copy each call (= the
         *  consumer can keep / queue it freely). */
        this.onCleanedSamples = null;
        this._lastClipLogMs = 0;

        // Per-load + per-startEffect resources.
        this._loaded = false;
        this._ac = null;
        this._micSource = null;
        this._farSource = null;
        this._workletNode = null;
        this._destination = null;
        this._emitNode = null;
        this._micRem = new Int16Array(0);
        this._farRem = new Int16Array(0);
        this._aecQueue = Promise.resolve();

        // Dropout counters fed by the emit worklet, split into two causes:
        //   空 (urMs)  = queue ran empty when process() fired
        //   溢 (trimMs) = cleaned chunks dropped by overflow trim after a
        //                 render-thread freeze (CPU). "Win" resets each 5 s
        //                 alive window; "Total" is cumulative for the badge.
        this._urWinUrMs = 0;
        this._urWinTrimMs = 0;
        this._urWinEvents = 0;
        this._urTotalMs = 0;
        this._urTotalEvents = 0;
    }

    /**
     * Pre-load the ONNX runtime + model AND set up the AudioWorklet modules.
     * Must be called AFTER setFarEndSource() so we know which AudioContext
     * to host the worklets in — sharing the same AC as the far-end source
     * is the only reliable way to get audio across (cross-AC delivery via
     * MediaStream is unreliable on Chrome for remote-peer streams).
     * lib-jitsi-meet's startEffect() contract is synchronous so all async
     * work (model fetch, addModule) MUST happen here.
     */
    async load() {
        if (this._loaded) return;

        if (!this._ac) {
            throw new Error('NkfAecEffect: setFarEndSource() must be called before load()');
        }

        if (!this._worker && !this.engine) {
            try {
                await this._initWorker();
                console.info('[nkf] ONNX inference running in Web Worker (off main thread)');
            } catch (e) {
                console.warn('[nkf] Worker init failed — falling back to main-thread engine:', e);
                this.engine = new NkfOnnxEngine();
                await this.engine.init({
                    modelUrl: this.modelUrl,
                    farDelaySamples: this.farDelaySamples
                });
            }
        }

        // workletPath は毎回おなじ URL なので module map が重複を潰してくれる。
        await this._ac.audioWorklet.addModule(this.workletPath);

        // emit 側は blob URL が毎回変わり module map が効かない。この AudioContext に
        // 入れ済みなら入れ直さない（入れ直しても中の try/catch で無害ではあるが、
        // 無駄な取得と評価を避ける）。
        const _dbgEmitFirst = !_emitModuleAdded.has(this._ac);

        console.info(`[nkf-dbg] effect#${this._effectId} load() ${_dbgAcId(this._ac)}`
            + ` emitワークレット=${_dbgEmitFirst ? '新規登録' : '登録済みなので省略'}`);
        if (!_emitModuleAdded.has(this._ac)) {
            const emitUrl = URL.createObjectURL(
                new Blob([ EMIT_WORKLET_SRC ], { type: 'text/javascript' }));

            await this._ac.audioWorklet.addModule(emitUrl);
            URL.revokeObjectURL(emitUrl);
            _emitModuleAdded.add(this._ac);
        }

        this._loaded = true;
    }

    /**
     * Spawn the inference Worker and wait for its 'ready' handshake.
     * Rejects on worker error or a 30 s timeout (ORT + model fetch inside
     * the worker can be slow on first load, but not THAT slow).
     */
    _initWorker() {
        // Reuse the page-wide Worker if one is already initialised for this
        // model — skips the ~1.5-2 s ORT+model reload on every mic-track
        // rebuild (camera toggle) and keeps the adaptive filter converged.
        if (_sharedWorker && _sharedWorkerModelUrl === this.modelUrl) {
            this._worker = _sharedWorker;
            // Register as a possible recipient. Do NOT take over playback here:
            // this effect has no audio graph yet, and claiming delivery at
            // construction is what silenced the effect that did have one.
            _sharedWorkerEffects.set(this._effectId, this);
            // Push our seed delay so the (persisted, converged) value wins over
            // whatever the previous owner last set.
            try {
                console.info(`[nkf-dbg] effect#${this._effectId} 共有ワーカーを再利用し`
                    + ` setDelay=${this.farDelaySamples} を書き込みます`
                    + '（このエフェクトはまだ音声グラフを持っていません）');
                this._worker.postMessage({ type: 'setDelay', farDelaySamples: this.farDelaySamples });
            } catch { /* ignore */ }
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            let w;
            try {
                w = new Worker(this.workerPath);
            } catch (e) {
                reject(e);
                return;
            }
            const timer = setTimeout(() => {
                w.terminate();
                reject(new Error('worker init timeout (30 s)'));
            }, 30000);
            w.onerror = e => {
                clearTimeout(timer);
                w.terminate();
                reject(new Error(e.message || 'worker script error'));
            };
            w.onmessage = ev => {
                const data = ev.data || {};
                if (data.type === 'ready') {
                    clearTimeout(timer);
                    // Route results to whichever effect currently owns playback,
                    // not necessarily the one that created the worker.
                    // Deliver each result to the effect that sent the chunk.
                    // The id we put on the way out comes back untouched.
                    w.onmessage = ev2 => {
                        const d = ev2.data || {};

                        // Answer to a re-init requested by resetSharedAecEngine.
                        if (d.type === 'ready' && _sharedWorkerReinit) {
                            const done = _sharedWorkerReinit;

                            _sharedWorkerReinit = null;
                            done(null);
                            return;
                        }
                        // Diagnosis reports are worker-wide, not per chunk, so
                        // they carry no id and the owner lookup below would drop
                        // them. Hand them to whichever effect is running.
                        if (d.type === 'skipdiag') {
                            const any = _sharedWorkerEffects.values().next().value;

                            if (any) any._onWorkerMessage(d);

                            return;
                        }

                        const target = _sharedWorkerEffects.get(_ownerIdOf(d.id));

                        // No target = the sender was torn down while this chunk
                        // was in flight. Dropping it is correct; handing it to
                        // some other effect would emit one meeting's audio into
                        // another effect's stream.
                        if (target) target._onWorkerMessage(d);
                        else if (d.type === 'error') console.error('[nkf] worker error:', d.message);
                    };
                    w.onerror = err => console.error('[nkf] worker runtime error:', err.message || err);
                    _sharedWorker = w;
                    _sharedWorkerModelUrl = this.modelUrl;
                    _sharedWorkerEffects.set(this._effectId, this);
                    this._worker = w;
                    resolve();
                } else if (data.type === 'error') {
                    clearTimeout(timer);
                    w.terminate();
                    reject(new Error(data.message));
                }
            };
            w.postMessage({
                type: 'init',
                modelUrl: new URL(this.modelUrl, location.href).href,
                farDelaySamples: this.farDelaySamples
            });
        });
    }

    _onWorkerMessage(data) {
        if (!data) return;
        if (data.type === 'result') {
            // Belt and braces: the dispatcher already addressed us, but emitting
            // another effect's audio into this stream is bad enough to check.
            if (_ownerIdOf(data.id) !== this._effectId) return;
            this._workerPending = Math.max(0, (this._workerPending | 0) - 1);

            if (typeof data.onnxMs === 'number') {
                this._onnxSum += data.onnxMs;
                if (data.onnxMs > this._onnxMax) this._onnxMax = data.onnxMs;
                this._onnxN++;
            }

            const _sent = this._sentAt.get(data.id);

            if (_sent !== undefined) {
                this._sentAt.delete(data.id);
                const _rtt = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - _sent;

                this._rttSum += _rtt;
                if (_rtt > this._rttMax) this._rttMax = _rtt;
                this._rttN++;
            }

            this._emitCleaned(data.cleaned);
        } else if (data.type === 'skipdiag') {
            this._logSkipDiag(data);
        } else if (data.type === 'error') {
            console.error('[nkf] worker error:', data.message);
        }
    }

    /**
     * Logs the Kalman-skip tally coming from the worker.
     *
     * Reads the 31.25 Hz warble question directly: the far reference decides,
     * per frame, whether the echo is subtracted at all, and a skipped frame goes
     * out unsubtracted (louder). One chunk is two frames, so if that decision
     * lands differently on the two slots the level alternates with a 512-sample
     * period — exactly the 31.25 Hz modulation measured in the recordings.
     *
     *   slot0 と slot1 の skip 率が離れている → 交互に切り替わっている（原因確定）
     *   両方とも 0% か 100%                   → この分岐は無関係。別を探す
     *
     * abs_mean が threshold のすぐ近くにいるかどうかも併せて出す。離れていれば
     * 判定は安定していて、揺れる余地がない。
     *
     * @param {Object} d - The worker's tally.
     * @returns {void}
     */
    _logSkipDiag(d) {
        const pct = (s) => (s.n ? (s.skips / s.n * 100) : 0).toFixed(1);
        const near = (s) => (s.absMin <= d.threshold && s.absMax >= d.threshold) ? ' ★境界付近' : '';
        const gap = Math.abs(Number(pct(d.slot0)) - Number(pct(d.slot1)));

        console.info(
            `[nkf][skip] ${d.frames}frames/${d.seconds.toFixed(1)}s`
            + ` | slot0 skip=${pct(d.slot0)}% (abs ${d.slot0.absMean.toExponential(1)}`
            + ` [${d.slot0.absMin.toExponential(1)}..${d.slot0.absMax.toExponential(1)}])${near(d.slot0)}`
            + ` | slot1 skip=${pct(d.slot1)}% (abs ${d.slot1.absMean.toExponential(1)}`
            + ` [${d.slot1.absMin.toExponential(1)}..${d.slot1.absMax.toExponential(1)}])${near(d.slot1)}`
            + ` | 反転=${d.flips}回 | 差=${gap.toFixed(1)}pt`
            + (gap > 10 ? '  ← 交互に切り替わっている' : '')
        );
    }

    /**
     * Report — once — that echo cancellation is judged to be working.
     *
     * OBSERVATION ONLY. No audio path reads this; it drives a toolbar label so
     * we can check the criteria against real meetings before acting on them.
     *
     * @param {string} reason - What decided it: 'calibration' | 'tracker' |
     * 'erle' | 'timeout'.
     * @returns {void}
     */
    _markAecReady(reason, working = true) {
        if (this._aecReady) return;              // one-shot
        this._aecReady = true;
        if (working) console.info(`[nkf] AEC ready (${reason})`);

        // With the hold armed, "ready" is not yet "transmitting": the mic stays
        // silent for one more grace period. The report waits for that same
        // moment on purpose — clearing the toolbar badge at readiness would tell
        // the user they are being heard while the far side still gets silence.
        if (this._txHold) {
            // Entering the grace window: the delay is settled, but we are still
            // sending silence. Report it so the signal can change rather than
            // sitting on the same "not ready" it showed a moment ago.
            if (this._onSettling) {
                try { this._onSettling(reason); } catch { /* ignore */ }
            }
            clearTimeout(this._txHoldTimer);
            this._txHoldTimer = setTimeout(() => {
                this._txHold = false;
                console.info(`[nkf] transmitting — hold released (${reason}, `
                    + `+${this._readyGraceMs}ms grace)`);
                this._notifyReady(working, reason);
            }, this._readyGraceMs);

            return;
        }

        this._notifyReady(working, reason);
    }

    /**
     * Fire the readiness callback. Split out of {@link _markAecReady} because
     * the hold defers it; the one-shot guard lives in the caller.
     *
     * @param {boolean} working - False when we stopped waiting without
     * cancellation actually being in effect.
     * @param {string} reason - What decided it.
     * @returns {void}
     */
    _notifyReady(working, reason) {
        if (this._onReady) {
            // `working` false = we stopped waiting, but cancellation is NOT in
            // effect. Reporting that as ready would hide the very failure the
            // signal exists to surface.
            try { this._onReady(working, reason); } catch { /* ignore */ }
        }
    }

    /**
     * Start the "not ready yet" window. Called when the effect starts.
     *
     * @param {number} [timeoutMs] - Safety valve; report ready anyway after this.
     * @returns {void}
     */
    _armReadyWatch(timeoutMs = 10000) {
        this._aecReady = false;

        // Start withholding the transmitted audio. Released one grace period
        // after readiness — including the 'timeout'/'stalled' paths below, so a
        // room where the delay is never measurable still gets its voice back
        // instead of staying silent for the whole call.
        clearTimeout(this._txHoldTimer);
        this._txHold = this._holdUntilReady;
        this._ambientMicPow = 0;
        this._ambientN = 0;
        this._readyChunksAtArm = this._chunkCount | 0;

        // ★ The timer must live OUTSIDE the audio callback.
        //   An earlier version checked the deadline inside _emitCleaned, which
        //   only runs when cleaned audio is actually produced — so in the one
        //   case the safety valve exists for (the AEC not running at all) it
        //   could never fire, and the toolbar sat at "preparing" forever while
        //   the call had no audio. A timer fires regardless.
        clearTimeout(this._readyTimer);
        this._readyTimer = setTimeout(() => {
            if (this._aecReady) return;

            // Distinguish "still converging" from "not processing at all":
            // if no chunk was emitted during the whole window, the audio path
            // is broken, not slow. Report it as such instead of pretending
            // cancellation came good.
            const stalled = ((this._chunkCount | 0) === (this._readyChunksAtArm | 0));

            if (stalled) {
                console.error('[nkf] AEC produced no audio for '
                    + `${Math.round(timeoutMs / 1000)}s — processing is stalled`);
            }
            this._markAecReady(stalled ? 'stalled' : 'timeout', !stalled);
        }, timeoutMs);
    }

    /**
     * Update the round-trip delay at runtime (e.g. after a re-calibration).
     */
    setFarDelaySamples(samples, readyReason) {
        this.farDelaySamples = (samples | 0) || 0;
        // A measured delay counts as "working"; the coarse outputLatency seed
        // does not (it is a guess, not evidence that echo is being removed), so
        // only callers that actually measured pass a reason.
        if (readyReason && this.farDelaySamples > 0) this._markAecReady(readyReason);
        if (this.engine) this.engine.setFarDelaySamples(this.farDelaySamples);
        if (this._worker) {
            console.info(`[nkf-dbg] effect#${this._effectId} setDelay=${this.farDelaySamples}（追従）`);
            this._worker.postMessage({ type: 'setDelay', farDelaySamples: this.farDelaySamples });
        }
        // Persist the (converged) delay so a rebuilt effect can be re-seeded.
        if (this._onDelayChange && this.farDelaySamples > 0) {
            try { this._onDelayChange(this.farDelaySamples); } catch { /* ignore */ }
        }
        _updateEffectStateDebug(this.farDelaySamples, !!(this.engine || this._worker));
    }

    /**
     * In-situ calibration that measures echo round-trip delay through THE
     * EXACT same audio path AEC uses at runtime.  Standalone calibrator.js
     * uses createMediaElementSource on a URL-loaded WAV; runtime uses
     * captureStream → createMediaStreamSource → mixGain.  These two paths
     * have different Web Audio internal latencies (often 30-50 ms apart),
     * which puts the calibrated delay outside NKF's L=4 (= 64 ms) tap
     * window — visible as cleaned ≈ mic (no cancellation) at runtime.
     *
     * This method plays the calibration WAV through the SAME mixGain that
     * carries remote audio, captures (mic, far) pairs from the already-
     * running worklet, and runs GCC-PHAT on them.  The delay it returns is
     * exactly the alignment NKF needs for runtime data.
     *
     * @param {object} opts
     * @param {string} opts.voiceUrl       URL of the calibration WAV.
     * @param {number} [opts.durationMs]   Capture window (default = WAV duration + 500 ms).
     * @returns {Promise<{delaySamples,delayMs,peakFloorRatio,sampleCount}>}
     */
    async runInSituCalibration({ voiceUrl, durationMs, volume = 1, maxPlayMs = 1200 }) {
        if (!this._workletNode || !this.farEndSource) {
            throw new Error('NkfAecEffect.runInSituCalibration: startEffect must run first');
        }
        // farEndSource is the mixGain handed in by RemoteFarEndMix; inject
        // calibration audio into it so the measured delay matches the
        // runtime far-end path bit-for-bit.
        const mixNode = this.farEndSource;

        // Build a calibration audio element + Web Audio node mirroring the
        // runtime far-end path (captureStream of an <audio>) so latencies
        // match bit-for-bit.
        const audio = new Audio(voiceUrl);
        audio.crossOrigin = 'anonymous';
        audio.preload = 'auto';
        audio.muted = false;
        // Audible native playback. mixGain is deliberately NOT wired to
        // ac.destination in the current topology, so the clip must play through
        // the element's own OS output to create a real acoustic echo — exactly
        // like the remote <audio> elements do. captureStream() below taps the
        // SAME element into mixGain as the far-end reference, so the measured
        // delay matches the runtime far path.
        audio.volume = Math.max(0, Math.min(1, volume));
        audio.style.display = 'none';
        document.body.appendChild(audio);
        await new Promise((res, rej) => {
            audio.addEventListener('canplaythrough', res, { once: true });
            audio.addEventListener('error', rej, { once: true });
            audio.load();
        });

        let captured;
        if (typeof audio.captureStream === 'function') {
            try { await audio.play(); } catch { /* gesture-driven retry */ }
            captured = audio.captureStream();
        } else {
            // Fallback: direct MediaElementSource (older browsers). MES reroutes
            // the element's audio into the graph, so also send it to
            // ac.destination to keep it audible (else no acoustic echo).
            const mes = this._ac.createMediaElementSource(audio);
            mes.connect(mixNode);
            mes.connect(this._ac.destination);
            try { await audio.play(); } catch {}
        }
        // Cap playback so the join chime stays short even if the clip is long.
        if (maxPlayMs > 0) {
            setTimeout(() => { try { audio.pause(); } catch { /* ignore */ } }, maxPlayMs);
        }
        let calSrc = null;
        if (captured) {
            calSrc = this._ac.createMediaStreamSource(captured);
            calSrc.connect(mixNode);
        }

        // Hijack the worklet's onmessage so we receive (mic, far) pairs
        // directly instead of feeding them through NKF.  We restore the
        // normal handler at the end.
        const micChunks = [];
        const farChunks = [];
        const origOnMessage = this._workletNode.port.onmessage;
        this._workletNode.port.onmessage = (ev) => {
            const data = ev.data;
            if (data?.__diag) return;
            if (data?.mic && data?.far) {
                micChunks.push(data.mic);
                farChunks.push(data.far);
            }
        };

        const dur = audio.duration && isFinite(audio.duration) ? audio.duration : 1.5;
        const playSec = maxPlayMs > 0 ? Math.min(dur, maxPlayMs / 1000) : dur;
        const waitMs = durationMs ?? ((playSec + 0.4) * 1000);
        await new Promise(r => setTimeout(r, waitMs));

        // Restore normal handler so AEC resumes processing.
        this._workletNode.port.onmessage = origOnMessage;
        try { calSrc?.disconnect(); } catch {}
        try { audio.pause(); audio.srcObject = null; audio.remove(); } catch {}

        // Concatenate captured chunks into Float64 mono signals at 16 kHz.
        let totalMic = 0, totalFar = 0;
        for (const m of micChunks) totalMic += m.length;
        for (const f of farChunks) totalFar += f.length;
        const n = Math.min(totalMic, totalFar);
        if (n < 4096) {
            throw new Error(`NkfAecEffect.runInSituCalibration: too few samples (${n})`);
        }
        const mic = new Float64Array(n), far = new Float64Array(n);
        let off = 0;
        for (const m of micChunks) {
            const k = Math.min(m.length, n - off); if (k <= 0) break;
            for (let i = 0; i < k; i++) mic[off + i] = m[i] / 32768;
            off += k;
        }
        off = 0;
        for (const f of farChunks) {
            const k = Math.min(f.length, n - off); if (k <= 0) break;
            for (let i = 0; i < k; i++) far[off + i] = f[i] / 32768;
            off += k;
        }

        const TARGET_SR = 16000;
        const maxLag = Math.min(Math.floor(0.5 * TARGET_SR), n - 1024);
        const { lag, peakFloorRatio } = gccPhat(far, mic, maxLag);
        return {
            delaySamples: lag,
            delayMs: lag * 1000 / TARGET_SR,
            peakFloorRatio,
            sampleCount: n
        };
    }

    /**
     * Attach the far-end (remote-mix) AudioNode.  Must be called before
     * load() so the effect knows which AudioContext to host its workletNode
     * in — sharing AC with the far-end source is the canonical NKF design,
     * and it's the only reliable path on Chrome (cross-AC MediaStream
     * bridging silently delivers no samples for remote peer streams).
     */
    setFarEndSource(audioNode) {
        if (!audioNode) {
            this.farEndSource = null;
            return;
        }
        if (this._ac && this._ac !== audioNode.context) {
            throw new Error('NkfAecEffect: cannot switch AudioContext after load(); pass a node from the original AC');
        }
        this.farEndSource = audioNode;
        this._ac = audioNode.context;
        // If startEffect already wired up, swap input 1.
        if (this._workletNode) {
            try { this.farEndSource.disconnect(this._workletNode, 0, 1); } catch {}
            try { this._farSource?.disconnect(); } catch {}
            this._farSource = null;
            this.farEndSource.connect(this._workletNode, 0, 1);
        }
    }

    /**
     * Compatibility shim for callers that previously handed us a
     * MediaStream.  We now require an AudioNode in the page's AC; using
     * MediaStream forces cross-AC routing which doesn't reliably deliver
     * remote-peer audio on Chrome.  This shim throws — use setFarEndSource
     * with an AudioNode instead.
     */
    setFarEndStream() {
        throw new Error('NkfAecEffect: setFarEndStream() is no longer supported; use setFarEndSource(audioNode)');
    }

    isEnabled(sourceLocalTrack) {
        // Audio-only effect.  lib-jitsi-meet calls this for every local
        // track (audio AND video) when picking which effect to apply, so
        // returning true for video tracks would crash startEffect() when
        // it tries to source audio out of the video MediaStream.
        const isAudio = !sourceLocalTrack
            || typeof sourceLocalTrack.isAudioTrack !== 'function'
            || sourceLocalTrack.isAudioTrack();
        const supported = typeof window !== 'undefined'
            && typeof window.AudioWorkletNode === 'function'
            && typeof WebAssembly === 'object';
        const enabled = isAudio && supported;
        const trackTag = sourceLocalTrack
            ? (sourceLocalTrack.isAudioTrack?.() ? 'audio' : 'video') + '/' + (sourceLocalTrack.getId?.() ?? '?')
            : '<no track>';
        console.info(`[nkf] isEnabled(${trackTag}) -> ${enabled}`);
        return enabled;
    }

    /**
     * @param {MediaStream} stream MediaStream whose audio track is the raw mic.
     * @returns {MediaStream} A new MediaStream with NKF-cleaned audio.
     *   Synchronous — async setup happens in load() which the integrator
     *   must await first.
     */
    startEffect(stream) {
        const inputTrackId = stream?.getAudioTracks?.()[0]?.id ?? '?';
        console.info(`[nkf] startEffect(stream-track=${inputTrackId}) — engaging AEC`);
        console.info(`[nkf-dbg] effect#${this._effectId} 開始 ${_dbgAcId(this._ac)}`
            + ` far-delay=${this.farDelaySamples} 受信登録数=${_sharedWorkerEffects.size}`);

        // 直前まで動いていた別のエフェクトを畳む。これを飛ばすと両方が共有エンジンへ
        // 流し込み、送信音が壊れる。停止は冪等なので、あとで lib-jitsi-meet が
        // 同じエフェクトを止めに来ても問題ない。
        if (_activeEffect && _activeEffect !== this) {
            const prev = _activeEffect;

            console.info(`[nkf] effect#${prev._effectId} がまだ動いていたので停止します`
                + `（共有エンジンは1個。2本並走すると送信音が壊れる）`);
            try {
                prev.stopEffect();
            } catch (e) {
                console.warn('[nkf] 旧エフェクトの停止に失敗:', e);
            }
        }
        _activeEffect = this;
        this._queueDepth = 0;
        if (!this._loaded) {
            throw new Error('NkfAecEffect: load() must be awaited before startEffect()');
        }
        if (!this.farEndSource) {
            throw new Error('NkfAecEffect: setFarEndSource() must be called before startEffect()');
        }
        // Claim the shared Worker: cleaned results now route to THIS effect's
        // playout. (load() already set this, but on a mic-track rebuild the old
        // effect's stopEffect and the new effect's startEffect can interleave.)
        if (this._worker) {
            // Re-register: stopEffect() removes us, and a warm restart (camera
            // toggle) comes back through here without another load().
            _sharedWorkerEffects.set(this._effectId, this);
        }
        // Resume in the background; the autoplay policy may have parked the
        // AC in 'suspended' since load() ran without a user gesture.
        if (this._ac.state === 'suspended') {
            void this._ac.resume();
        }

        this._micSource = this._ac.createMediaStreamSource(stream);
        this._workletNode = new AudioWorkletNode(this._ac, 'sync-aec', {
            numberOfInputs: 2, numberOfOutputs: 1, outputChannelCount: [ 2 ]
        });
        this._micSource.connect(this._workletNode, 0, 0);
        // Direct AudioNode connection — same AC, no MediaStream bridging.
        this.farEndSource.connect(this._workletNode, 0, 1);

        // Output: cleaned 16 kHz Int16 from the engine, replayed at AC sr via
        // the emit-worklet registered in load().  Routed to a
        // MediaStreamDestination so the consumer can swap it in via
        // JitsiLocalTrack.setEffect().
        this._emitNode = new AudioWorkletNode(this._ac, 'nkf-emit', { numberOfOutputs: 1 });
        // Receive dropout (underrun) reports from the emit worklet so we can
        // prove 音の途切れ live on-screen + in the alive log.
        this._emitNode.port.onmessage = (e) => {
            const d = e.data;
            if (d && d.__underrun) { this._onEmitUnderrun(d); }
        };
        this._destination = this._ac.createMediaStreamDestination();

        // Clean post-AEC makeup chain: GainNode (smooth level) → Compressor
        // (native brick-wall-ish limiter). Replaces per-sample JS gain/clip.
        if (this._agcEnabled) {
            this._makeupGainNode = this._ac.createGain();
            this._makeupGainNode.gain.value = 1;
            this._limiterNode = this._ac.createDynamicsCompressor();
            this._limiterNode.threshold.value = -1.5;   // dBFS ceiling
            this._limiterNode.knee.value = 0;
            this._limiterNode.ratio.value = 20;         // hard-ish limiting
            this._limiterNode.attack.value = 0.003;
            this._limiterNode.release.value = 0.12;
            this._makeupGainNode.connect(this._limiterNode);
            this._limiterNode.connect(this._destination);
        }

        // Wire _emitNode → [RNNoise] → makeup/destination. Synchronous with the
        // NS stage bypassed if its worklet node isn't built yet; if NS is meant
        // to be ON we build the node asynchronously and re-wire once ready
        // (startEffect must stay synchronous per lib-jitsi-meet's contract).
        this._wireOutputChain();
        if (this._nsEnabled) {
            this._ensureNsNode()
                .then(() => this._wireOutputChain())
                .catch(e => console.error('[nkf] NS init err:', e));
        }

        // Diagnostic transmit-path meter (opt-in via ?nkfMeter=1).
        this._maybeStartTransmitMeter();

        // Process loop — chunks (mic, far) Int16 pairs from the worklet at
        // 32 ms cadence (post optimisation).  Run NKF at HOP=256 (= 16 ms)
        // granularity, serialised via a promise queue so the async ORT calls
        // don't race on the engine state across overlapping events.
        this._workletNode.port.onmessage = (ev) => {
            const data = ev.data;
            if (data && data.__diag) {
                console.info(
                    `[nkf/worklet-diag] far chCount=${data.farChCount}`
                    + ` farLen=${data.farLen} farAbsMax=${data.farAbsMax.toFixed(4)}`
                    + ` mic chCount=${data.micChCount} micLen=${data.micLen}`
                );
                return;
            }

            // Passive delay tracking: observe every (mic, far) pair (both
            // modes) to keep the far-delay aligned without a calibration step.
            if (this._delayTracker && data.mic && data.far) {
                this._delayTracker.push(data.mic, data.far);
            }

            // User AEC ON/OFF toggle. When OFF, pass the raw mic through (still
            // in-order via the serial queue) so ON/OFF audio quality can be
            // A/B compared. Works for both worker and main-thread modes.
            if (this._userBypass) {
                this._aecQueue = this._aecQueue
                    .then(() => { this._preChunk(data.mic, data.far); this._emitCleaned(data.mic); })
                    .catch(e => console.error('[nkf] passthrough err:', e));
                return;
            }

            // Worker mode: framing + inference happen inside the Worker;
            // just forward the chunk (with backpressure).
            if (this._worker) {
                this._sendChunkToWorker(data);
                return;
            }

            // When ONNX inference can't keep up with real-time, pass the raw
            // mic through in-order (via the same serial queue) instead of
            // dropping — audio stays continuous, echo momentarily uncancelled.
            const MAX_QUEUE_DEPTH = 4;
            this._queueDepth = (this._queueDepth | 0) + 1;
            if (this._queueDepth > MAX_QUEUE_DEPTH) {
                this._queueDepth--;
                this._noteBypass();
                this._aecQueue = this._aecQueue
                    .then(() => {
                        this._preChunk(data.mic, data.far);
                        this._emitCleaned(data.mic);
                    })
                    .catch(e => console.error('[nkf] bypass err:', e));
                return;
            }
            this._aecQueue = this._aecQueue
                .then(() => this._handleChunk(data))
                .catch(e => console.error('[nkf] queue err:', e))
                .finally(() => { this._queueDepth = Math.max(0, (this._queueDepth | 0) - 1); });
        };

        // Reset chunk-level diagnostics so the next "still alive" log
        // reflects this run only.
        this._chunkCount = 0;
        this._statsLastReportMs = 0;
        this._micPeakWindow = 0;
        this._farPeakWindow = 0;
        this._cleanedPeakWindow = 0;
        this._bypassWindow = 0;

        // Passive continuous delay tracking (replaces one-shot calibration).
        // Seed a coarse starting offset from the known Web Audio output latency
        // so the first ~second is in the ballpark; the tracker then refines it
        // from the real remote speech and follows drift for the rest of the call.
        if (this._autoDelayTrack) {
            if (!this.farDelaySamples) {
                const outLat = (this._ac.outputLatency || 0) + (this._ac.baseLatency || 0);
                const seed = Math.round(outLat * 16000);
                if (seed > 0 && seed < 16000) this.setFarDelaySamples(seed);
            }
            this._armReadyWatch();
            try { this._delayTracker?.stop(); } catch { /* ignore */ }
            this._delayTracker = new DelayTracker({
                initialDelay: this.farDelaySamples,
                onDelay: (d, info) => {
                    console.info(`[nkf] auto-delay → ${d} samples (${info.delayMs.toFixed(1)} ms, conf=${info.peakFloorRatio.toFixed(1)}, ${info.phase})`);
                    // 'warmup' is the high-confidence single-shot commit; 'drift'
                    // needs 3 agreeing estimates. Either means we measured a real
                    // echo. 'jump' is a mid-call route change, not a first lock.
                    this.setFarDelaySamples(d, info.phase === 'jump' ? null : 'tracker');
                }
            });

            // A moving outputLatency means the OS re-routed / re-buffered the
            // output path, so the echo delay likely moved with it — re-enter
            // the tracker's fast-follow window instead of leaking echo while
            // the slow stability gate catches up.
            if (this._outLatPollId) clearInterval(this._outLatPollId);
            this._lastOutLatMs = (this._ac.outputLatency || 0) * 1000;
            this._outLatPollId = setInterval(() => {
                const lat = (this._ac?.outputLatency || 0) * 1000;
                if (Math.abs(lat - this._lastOutLatMs) > 5) {
                    console.info(`[nkf] outputLatency ${this._lastOutLatMs.toFixed(0)} → ${lat.toFixed(0)} ms — rearming delay tracker`);
                    this._delayTracker?.rearm();
                }
                this._lastOutLatMs = lat;
            }, 2000);
        }

        const outId = this._destination.stream.getAudioTracks()[0]?.id ?? '?';
        console.info(`[nkf] startEffect: returning cleaned MediaStream track=${outId}, far-delay=${this.farDelaySamples} samples`);
        if (typeof document !== 'undefined') {
            window.__nkfAecRunning = true;
            window.__nkfEffect = this;
            if (!window.__nkfToggleAec) {
                window.__nkfToggleAec = () => {
                    const e = window.__nkfEffect; if (!e) return;
                    e._userBypass = !e._userBypass;
                    console.info('[nkf] AEC ' + (e._userBypass ? 'OFF（素通し）' : 'ON'));
                    return !e._userBypass;
                };
            }
            document.dispatchEvent(new CustomEvent('nkf-aec-started'));
        }

        // ── shared debug snapshot — must MATCH the demo's identical print
        //    so demo vs jitsi can be diffed line-by-line ──────────────────
        try {
            const micTrack = stream?.getAudioTracks?.()[0];
            const micSettings = micTrack?.getSettings?.() ?? {};
            const fe = this.farEndSource;
            console.info('[nkf-debug] startEffect snapshot', {
                where:           'jitsi',
                acSampleRate:    this._ac.sampleRate,
                acState:         this._ac.state,
                farEndKind:      fe?.constructor?.name,
                farEndAcSR:      fe?.context?.sampleRate,
                farEndAcSame:    fe?.context === this._ac,
                farEndNumOuts:   fe?.numberOfOutputs,
                farEndChCount:   fe?.channelCount,
                micTrackSR:      micSettings.sampleRate,
                micTrackChs:     micSettings.channelCount,
                micTrackDevice:  micSettings.deviceId,
                farDelaySamples: this.farDelaySamples
            });
        } catch (e) { console.warn('[nkf-debug] snapshot failed:', e); }

        return this._destination.stream;
    }

    /**
     * Per-chunk pre-processing shared by the worker and main-thread paths:
     * clip detection ("音がでかすぎ" warning) + mic/far debug-waveform push
     * + peak-window bookkeeping for the periodic alive log.
     */
    _preChunk(mic, far) {
        let micPeak = 0, farPeak = 0;
        for (let i = 0; i < mic.length; i++) {
            const a = mic[i] < 0 ? -mic[i] : mic[i];
            if (a > micPeak) micPeak = a;
        }
        for (let i = 0; i < far.length; i++) {
            const a = far[i] < 0 ? -far[i] : far[i];
            if (a > farPeak) farPeak = a;
        }
        if (micPeak > CLIP_THRESHOLD || farPeak > CLIP_THRESHOLD) {
            const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            if (now - this._lastClipLogMs > CLIP_THROTTLE_MS) {
                this._lastClipLogMs = now;
                const which = [];
                if (micPeak > CLIP_THRESHOLD) which.push(`mic=${micPeak}`);
                if (farPeak > CLIP_THRESHOLD) which.push(`far=${farPeak}`);
                console.warn(`[nkf] 音がでかすぎ (${which.join(', ')} / 32767)`);
            }
        }

        // Visual debug overlay — push BEFORE any buffer transfer detaches
        // the arrays.
        _nkfDebugPush('mic', mic);
        _nkfDebugPush('far', far);
        if (micPeak > this._micPeakWindow) this._micPeakWindow = micPeak;
        if (farPeak > this._farPeakWindow) this._farPeakWindow = farPeak;

        // ── Live ERLE: accumulate this chunk's mic/far power and queue it to be
        //    paired with the cleaned chunk in _emitCleaned.  ERLE is measured
        //    only while the far-end (AI) is active.  (1073741824 = 32768^2)
        let micSq = 0, farSq = 0;
        for (let i = 0; i < mic.length; i++) micSq += mic[i] * mic[i];
        for (let i = 0; i < far.length; i++) farSq += far[i] * far[i];
        const micPow = micSq / (mic.length * 1073741824);
        const farPow = farSq / (far.length * 1073741824);
        if (!this._erleQ) this._erleQ = [];
        this._erleQ.push({ farActive: farPow > 1e-5, mic: micPow });
        if (this._erleQ.length > 128) this._erleQ.shift();
    }

    /**
     * Forward one (mic, far) chunk to the inference Worker.  Framing and
     * remainder handling live inside the Worker; cleaned samples come back
     * via _onWorkerMessage → _emitCleaned.  Backpressure: drop the chunk
     * when too many are still in flight (inference slower than real-time).
     */
    _sendChunkToWorker({ mic, far }) {
        this._preChunk(mic, far);

        // Overload → don't drop (audible gap); mark the chunk 'bypass' so
        // the worker returns the raw mic without inference, in order.
        // Forced bypass wins over the real overload check so the artifact can be
        // summoned without having to starve the machine first. Random rather
        // than every-Nth on purpose: real overload arrives in irregular bursts,
        // and a fixed period would add its own tone to what we are listening for.
        const forced = _forceBypassPct > 0 && (Math.random() * 100) < _forceBypassPct;
        const bypass = forced || (this._workerPending | 0) >= MAX_PENDING;

        if (bypass) this._noteBypass();
        this._workerPending = (this._workerPending | 0) + 1;
        if (this._workerPending > this._pendingMax) this._pendingMax = this._workerPending;
        this._chunkId = (this._chunkId | 0) + 1;
        this._sentAt.set(`${this._effectId}:${this._chunkId}`,
            (typeof performance !== 'undefined' ? performance.now() : Date.now()));
        this._worker.postMessage(
            // "<effectId>:<seq>" — the Worker echoes this verbatim, which is how
            // the result finds its way back to the effect that sent the input.
            { type: 'chunk', id: `${this._effectId}:${this._chunkId}`, mic, far, bypass },
            [ mic.buffer, far.buffer ]
        );
    }

    /** Throttled overload logging + per-window bypass counter for the alive log. */
    _noteBypass() {
        this._bypassWindow = (this._bypassWindow | 0) + 1;
        const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        if (now - (this._lastBypassLogMs || 0) > 2000) {
            this._lastBypassLogMs = now;
            console.warn('[nkf] 過負荷 — AECをバイパスして素通し中（音は途切れないがエコーは消えない）');
        }
    }

    async _handleChunk({ mic, far }) {
        this._preChunk(mic, far);

        const fs = this.engine.frameSize;
        const micCat = new Int16Array(this._micRem.length + mic.length);
        micCat.set(this._micRem); micCat.set(mic, this._micRem.length);
        const farCat = new Int16Array(this._farRem.length + far.length);
        farCat.set(this._farRem); farCat.set(far, this._farRem.length);
        const nFrames = Math.floor(micCat.length / fs);
        if (nFrames === 0) {
            this._micRem = micCat; this._farRem = farCat; return;
        }
        const out = new Int16Array(nFrames * fs);
        for (let k = 0; k < nFrames; k++) {
            const start = k * fs;
            const clean = await this.engine.processFrame(
                micCat.subarray(start, start + fs),
                farCat.subarray(start, start + fs),
            );
            out.set(clean, start);
        }
        this._micRem = micCat.slice(nFrames * fs);
        this._farRem = farCat.slice(nFrames * fs);

        this._emitCleaned(out);
    }

    /**
     * Post-processing shared by the worker and main-thread paths: listener
     * callback, debug-waveform push, periodic alive log, and hand-off to the
     * emit worklet (which detaches `out` via buffer transfer — so this must
     * run LAST).
     */
    _emitCleaned(out) {
        // Hand a copy to any listener BEFORE transferring to the emit worklet
        // (postMessage with [out.buffer] detaches `out`).
        if (this.onCleanedSamples) {
            try { this.onCleanedSamples(new Int16Array(out)); }
            catch (e) { console.warn('[nkf] onCleanedSamples threw:', e); }
        }
        // Aggregate stats for a periodic "alive" log so the operator can see
        // NKF is actually processing audio frames (not just loaded).
        let cleanedPeak = 0;
        for (let i = 0; i < out.length; i++) {
            const a = out[i] < 0 ? -out[i] : out[i];
            if (a > cleanedPeak) cleanedPeak = a;
        }

        // Show the cleaned waveform AFTER the makeup gain (= what is actually sent).
        _nkfDebugPush('cleaned', out,
            (this._agcEnabled && this._makeupGainNode) ? this._makeupGainNode.gain.value : 1);
        this._chunkCount = (this._chunkCount | 0) + 1;
        if (cleanedPeak > this._cleanedPeakWindow) this._cleanedPeakWindow = cleanedPeak;

        // ── Live ERLE: pair this cleaned chunk with its mic (FIFO) and, while
        //    far was active, accumulate mic/cleaned power for the window ERLE.
        let clnSq = 0;
        for (let i = 0; i < out.length; i++) clnSq += out[i] * out[i];
        const clnPow = clnSq / (out.length * 1073741824);
        const _q = (this._erleQ && this._erleQ.length) ? this._erleQ.shift() : null;
        if (_q && _q.farActive) {
            this._erleMicSum = (this._erleMicSum || 0) + _q.mic;
            this._erleClnSum = (this._erleClnSum || 0) + clnPow;
            this._erleN = (this._erleN || 0) + 1;
        }

        // ── Readiness (OBSERVATION ONLY — nothing in the audio path reads it).
        //
        //    ERLE is a ratio, so a quiet far end gives a low number even when
        //    cancellation is perfect: the echo never rose above the room noise,
        //    so removing it barely changes the mic power. Judging "not working"
        //    from that would keep us at "preparing" for a soft-spoken AI.
        //
        //    So ERLE may only ever CONFIRM readiness early, never delay it:
        //      - accumulate only where the echo is clearly above ambient
        //      - a low or unmeasurable ERLE is "no opinion", not "failing"
        //      - the other criteria (calibration / tracker / timeout) still fire
        if (!this._aecReady) {
            if (_q && !_q.farActive) {
                // Far end silent → this is room noise; learn the floor.
                this._ambientMicPow = ((this._ambientMicPow || 0) * (this._ambientN || 0) + _q.mic)
                    / ((this._ambientN || 0) + 1);
                this._ambientN = Math.min((this._ambientN || 0) + 1, 200);
            } else if (_q && _q.farActive
                    && this._ambientN > 10
                    && _q.mic > this._ambientMicPow * 10) {   // echo ≥ 10 dB over ambient
                this._erleReadyMicSum = (this._erleReadyMicSum || 0) + _q.mic;
                this._erleReadyClnSum = (this._erleReadyClnSum || 0) + clnPow;
                this._erleReadyN = (this._erleReadyN || 0) + 1;

                if (this._erleReadyN >= 50 && this._erleReadyClnSum > 0) {
                    const db = 10 * Math.log10(this._erleReadyMicSum / this._erleReadyClnSum);

                    if (db >= 10) this._markAecReady('erle');
                }
            }
        }
        const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
        if (!this._statsLastReportMs) this._statsLastReportMs = nowMs;
        if (nowMs - this._statsLastReportMs > 5000) {
            const elapsed = ((nowMs - this._statsLastReportMs) / 1000).toFixed(1);
            const _erleDb = (this._erleN > 0 && this._erleClnSum > 0)
                ? 10 * Math.log10(this._erleMicSum / this._erleClnSum) : null;
            console.info(
                `[nkf] alive: ${this._chunkCount} chunks in ${elapsed}s`
                + ` | mic-peak=${this._micPeakWindow}`
                + ` far-peak=${this._farPeakWindow}`
                + ` cleaned-peak=${this._cleanedPeakWindow}`
                + ` (32767=full)`
                + ` far-delay=${this.farDelaySamples}`
                + ` ERLE=${_erleDb != null ? _erleDb.toFixed(1) + 'dB' : '-'} (${this._erleN | 0}f)`
                + ` bypassed=${this._bypassWindow | 0}`
                + ` 途切れ=空${Math.round(this._urWinUrMs)}ms/溢${Math.round(this._urWinTrimMs)}ms(${this._urWinEvents | 0}回)`
                + ` キュー残=最小${this._qMinWindow === 99 ? '-' : this._qMinWindow}`
                + `/平均${this._qAvgN ? (this._qAvgSum / this._qAvgN).toFixed(1) : '-'}/上限${this._qCap}`
                + ` 未処理=最大${this._pendingMax | 0}/上限${MAX_PENDING}`
                + ` 推論=${this._onnxN ? (this._onnxSum / this._onnxN).toFixed(1) : '-'}ms`
                + `(最大${this._onnxMax.toFixed(1)})`
                + ` 往復=${this._rttN ? (this._rttSum / this._rttN).toFixed(1) : '-'}ms`
                + `(最大${this._rttMax.toFixed(1)})`
                + ` gain=${(this._agcEnabled && this._makeupGainNode) ? this._makeupGainNode.gain.value.toFixed(1) : '-'}`
                + (this._worker ? ' [worker]' : ' [main]')
            );
            _updateEffectStateDebug(this.farDelaySamples, !!(this.engine || this._worker), _erleDb);
            // Overload → user-facing card. When ONNX can't keep real-time the
            // chunk is bypassed (raw mic passes, echo not removed); a high
            // bypass rate over the window means this device can't keep up.
            const _byp = this._bypassWindow | 0;
            const _bypRate = this._chunkCount > 0 ? _byp / this._chunkCount : 0;
            if (_bypRate > 0.08) this._overloadStreak = (this._overloadStreak | 0) + 1;
            else this._overloadStreak = 0;
            _nkfSetOverload(this._overloadStreak >= 1, `処理落ち ${(_bypRate * 100).toFixed(0)}%（${_byp}/${this._chunkCount} フレーム）`);
            // ── Dropout warning ────────────────────────────────────────
            // 空 (queue ran dry → silence went out) and 溢 (queue overflowed →
            // cleaned audio was thrown away) are both holes the far side hears,
            // so they are judged together as one "share of this window that
            // never arrived".
            const _lossMs = (this._urWinUrMs || 0) + (this._urWinTrimMs || 0);
            const _lossPct = _lossMs / (nowMs - this._statsLastReportMs) * 100;

            if (_lossPct >= this._dropoutWarnPct) {
                this._dropoutOverRuns++;
                this._dropoutUnderRuns = 0;
            } else if (_lossPct <= this._dropoutClearPct) {
                this._dropoutUnderRuns++;
                this._dropoutOverRuns = 0;
            } else {
                // Between the two thresholds: hold whatever we are showing.
                this._dropoutOverRuns = 0;
                this._dropoutUnderRuns = 0;
            }

            if (!this._dropoutOn && this._dropoutOverRuns >= 2) {
                this._dropoutOn = true;
                console.warn(`[nkf] 音の途切れを検知 — 欠落 ${_lossPct.toFixed(1)}%`);
                if (this._onDropout) {
                    try { this._onDropout(true, _lossPct); } catch { /* ignore */ }
                }
            } else if (this._dropoutOn && this._dropoutUnderRuns >= 2) {
                this._dropoutOn = false;
                console.info('[nkf] 音の途切れが収まりました');
                if (this._onDropout) {
                    try { this._onDropout(false, _lossPct); } catch { /* ignore */ }
                }
            }

            // Snapshot for the media-stats panel. These are only computed once
            // per window, so the panel reads the last completed one rather than
            // recomputing from counters that are about to be cleared.
            _mediaLast = {
                chunksPerSec: this._chunkCount / ((nowMs - this._statsLastReportMs) / 1000),
                bypassRate: _bypRate,
                bypassed: _byp,
                chunks: this._chunkCount,
                erleDb: _erleDb,
                farDelaySamples: this.farDelaySamples,
                worker: Boolean(this._worker),
                // Pipeline health — how close we are to losing audio, and where
                // the time goes when we do.
                qMin: this._qMinWindow === 99 ? null : this._qMinWindow,
                qAvg: this._qAvgN ? this._qAvgSum / this._qAvgN : null,
                qCap: this._qCap,
                pendingMax: this._pendingMax | 0,
                pendingCap: MAX_PENDING,
                onnxAvg: this._onnxN ? this._onnxSum / this._onnxN : null,
                onnxMax: this._onnxN ? this._onnxMax : null,
                rttAvg: this._rttN ? this._rttSum / this._rttN : null,
                rttMax: this._rttN ? this._rttMax : null,
                at: nowMs
            };
            this._chunkCount = 0;
            this._statsLastReportMs = nowMs;
            this._micPeakWindow = 0;
            this._farPeakWindow = 0;
            this._cleanedPeakWindow = 0;
            this._bypassWindow = 0;
            this._urWinUrMs = 0;
            this._urWinTrimMs = 0;
            this._urWinEvents = 0;
            this._erleMicSum = 0;
            this._erleClnSum = 0;
            this._erleN = 0;
            this._pendingMax = 0;
            this._qMinWindow = 99;
            this._qAvgSum = 0;
            this._qAvgN = 0;
            this._onnxSum = 0;
            this._onnxMax = 0;
            this._onnxN = 0;
            this._rttSum = 0;
            this._rttMax = 0;
            this._rttN = 0;
            // Anything still unanswered is either in flight or was dropped with
            // its sender; either way it must not accumulate across windows.
            if (this._sentAt.size > 64) this._sentAt.clear();
        }

        // ── Post-AEC makeup gain ─────────────────────────────────────────────
        // Track the cleaned RMS and drive the GainNode toward a voice-standard
        // target (~-18 dBFS). The gain ramps smoothly (setTargetAtTime, no
        // zipper) and the DynamicsCompressor limits peaks (no clip distortion).
        // Measured AFTER ERLE so cancellation numbers stay honest.
        if (this._agcEnabled && this._agcActive && this._makeupGainNode && this._ac) {
            const TARGET = 0.12, MAX_G = 24;            // ~-18 dBFS RMS, +27 dB cap
            const rms = Math.sqrt(clnPow);
            // NEAR-END gate. During far-end-only periods (AI talking) the
            // "cleaned" signal is just residual echo. Adapting/raising the gain
            // there amplifies that residual into a loud「うう」artifact AND
            // ratchets _agcLevel so the user's real voice later comes out tiny
            // (the reported "時間が経つと変になる"). So only track the level and
            // move the gain when the far-end is NOT active; otherwise HOLD the
            // current gain. _q carries this chunk's far-active flag (from _erleQ).
            const nearActive = _q ? !_q.farActive : true;
            if (nearActive) {
                if (rms > 0.01) {                       // user actually audible
                    const a = 0.02;
                    this._agcLevel = this._agcLevel ? this._agcLevel * (1 - a) + rms * a : rms;
                }
                const target = this._agcLevel > 1e-4
                    ? Math.max(1, Math.min(MAX_G, TARGET / this._agcLevel)) : 1;
                try { this._makeupGainNode.gain.setTargetAtTime(target, this._ac.currentTime, 0.4); }
                catch { /* ignore */ }
            }
            // else: far-end active → hold gain, never amplify residual echo.
        }

        if (this._emitNode) {
            // Silence the TRANSMITTED stream while the hold is armed.
            //
            // Zeroed HERE — after the stats / ERLE / AGC above — on purpose:
            // readiness is decided from the cleaned signal, so muting earlier
            // would starve the very measurement that ends the hold and leave the
            // call silent until the safety timeout.
            if (this._txHold) out.fill(0);
            this._emitNode.port.postMessage(out, [ out.buffer ]);
        }
    }

    /**
     * Handles a dropout (underrun) report from the emit worklet: the queue ran
     * dry and silence went out on the TRANSMITTED stream (= 音の途切れ the far
     * side hears). Accumulates window + cumulative counters and drives the live
     * on-screen dropout badge so the symptom can be proven without a debugger.
     *
     * @param {{samples:number, events:number, windowSamples:number}} d
     * @returns {void}
     */
    _onEmitUnderrun(d) {
        const urMs = d.urMs || 0;
        const trimMs = d.trimMs || 0;
        const ev = (d.urEvents | 0) + (d.trimEvents | 0);
        this._urWinUrMs += urMs;
        this._urWinTrimMs += trimMs;
        this._urWinEvents += ev;

        if (typeof d.qMin === 'number' && d.qMin < this._qMinWindow) this._qMinWindow = d.qMin;
        if (typeof d.qAvg === 'number') { this._qAvgSum += d.qAvg; this._qAvgN++; }
        if (typeof d.qCap === 'number') this._qCap = d.qCap;

        // Feed the rolling window the media-stats panel reads. The _urWin*
        // counters above are reset every 5 s for the alive log, so a panel
        // polling them would show a sawtooth; this keeps a real moving window.
        _mediaUrRing.push({
            at: (typeof performance !== 'undefined' ? performance.now() : Date.now()),
            urMs,
            trimMs,
            events: ev
        });
        this._urTotalMs += urMs + trimMs;
        this._urTotalEvents += ev;
        _nkfSetUnderrunBadge(this._urTotalEvents, this._urTotalMs, ev > 0);
    }

    /**
     * (Re)connects the output chain _emitNode → [RNNoise] → makeup/destination
     * according to the current _nsEnabled flag. Idempotent; safe to call on
     * every toggle without tearing down the AEC graph.
     *
     * @returns {void}
     */
    _wireOutputChain() {
        if (!this._emitNode) {
            return;
        }
        try { this._emitNode.disconnect(); } catch { /* ignore */ }
        try { this._nsNode?.disconnect(); } catch { /* ignore */ }

        // Tail node that feeds the makeup gain (or destination when AGC off).
        const useNs = Boolean(this._nsEnabled && this._nsNode);
        const tail = useNs ? this._nsNode : this._emitNode;

        if (useNs) {
            this._emitNode.connect(this._nsNode);
        }
        if (this._agcEnabled && this._makeupGainNode) {
            tail.connect(this._makeupGainNode);
        } else {
            tail.connect(this._destination);
        }
    }

    /**
     * Transmit-path meter (diagnostic, opt-in). Passively taps the graph at the
     * stages that matter for "does the echo actually reach the sent audio":
     *   far  → mic → NKF出力(ゲイン前) → ゲイン後 → 送信(リミッター後)
     * and shows their live RMS (dBFS) in a bottom-right overlay. Analysers are
     * side-branches (they never alter the signal). Enabled only when the page
     * URL has `?nkfMeter=1` (or localStorage 'nkf-meter'='1'), so it is off for
     * normal users and shareable as a link.
     *
     * @returns {void}
     */
    _maybeStartTransmitMeter() {
        if (typeof window === 'undefined' || !this._ac) {
            return;
        }
        let on = this._meterWanted === true;

        // URL パラメータは Jitsi が入室時にURLを書き換えると消えるので、単独では
        // 頼れない。ツールバーからの指定(_meterWanted)を第一にして、URL と
        // localStorage は初回起動時の入口として残す。
        try {
            on = on || /[?&]nkfMeter=1\b/.test(window.location.search)
                || window.localStorage?.getItem('nkf-meter') === '1';
        } catch { /* ignore */ }
        if (!on || this._meter) {
            return;
        }

        const mk = node => {
            if (!node) return null;
            const a = this._ac.createAnalyser();
            a.fftSize = 1024;
            try { node.connect(a); } catch { return null; }
            return a;
        };
        // NKF出力(ゲイン前) = whatever feeds the makeup gain (NS tail or emit).
        const preNode = (this._nsEnabled && this._nsNode) ? this._nsNode : this._emitNode;
        const taps = {
            far: mk(this.farEndSource),
            mic: mk(this._micSource),
            pre: mk(preNode),
            post: mk(this._makeupGainNode),
            // 送信物 = リミッター後（AGC OFF時はNKF出力が直接destinationへ）。
            tx: mk(this._limiterNode || preNode)
        };
        // 周波数分解能を上げる。1024点(48kHz で ~47Hz/bin)では「キーン」が
        // どの周波数かを言い当てられず、折り返しなのか元からある音なのかを
        // 区別できない。4096点なら ~12Hz/bin。
        for (const a of Object.values(taps)) {
            if (a) a.fftSize = 4096;
        }

        const buf = new Float32Array(4096);
        const spec = new Float32Array(2048);

        /**
         * そのタップで一番強い成分の周波数と大きさ。
         *
         * 「キーン」がどこで生まれたかは、鳴っているかどうかではなく
         * 周波数が途中で変わるかどうかで決まる。折り返しなら入口(mic)と
         * 出口(送信)で周波数が違う(例 10kHz → 6kHz)。部屋やマイク由来なら
         * 入口から出口まで同じ周波数で通る。エンジンが作っているなら入口に
         * 無いものが出口に現れる。だから各点の主成分を並べて見せる。
         *
         * @param {AnalyserNode} a - 対象のタップ。
         * @returns {{hz: number, db: number}|null}
         */
        const peakHz = a => {
            if (!a) return null;
            a.getFloatFrequencyData(spec);
            const n = a.frequencyBinCount;
            const binHz = this._ac.sampleRate / a.fftSize;
            let bi = -1, bv = -Infinity;

            // 直流付近は電源ハムや直流オフセットで常に大きく、探したいものでは
            // ないので 150Hz 以下は見ない。
            const from = Math.ceil(150 / binHz);

            for (let i = from; i < n; i++) {
                if (spec[i] > bv) { bv = spec[i]; bi = i; }
            }
            if (bi < 0 || bv < -100) return null;

            // 頂点の左右で放物線を当てて、bin の刻みより細かく推定する。
            let d = 0;
            if (bi > 0 && bi < n - 1) {
                const l = spec[bi - 1], c = spec[bi], r = spec[bi + 1];
                const den = l - (2 * c) + r;

                if (den !== 0) d = 0.5 * (l - r) / den;
            }

            return { hz: (bi + d) * binHz, db: bv };
        };

        const rmsDb = a => {
            if (!a) return null;
            a.getFloatTimeDomainData(buf);
            let s = 0;
            for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
            const rms = Math.sqrt(s / buf.length);
            return rms > 1e-6 ? 20 * Math.log10(rms) : -120;
        };

        const el = document.createElement('div');
        el.id = 'nkf-tx-meter';
        el.style.cssText =
            'position:fixed;right:12px;bottom:12px;z-index:100002;'
            + 'background:rgba(10,14,22,.92);color:#dfe6f2;border:1px solid #2a3550;'
            + 'border-radius:10px;padding:10px 12px;'
            + 'font:12px/1.5 ui-monospace,Menlo,Consolas,monospace;min-width:220px;'
            + 'box-shadow:0 8px 24px rgba(0,0,0,.5)';
        document.body.appendChild(el);

        const fmt = v => (v == null ? ' -- ' : (v <= -119 ? '  無音' : `${v.toFixed(0).padStart(4)}dB`));
        const timer = setInterval(() => {
            const far = rmsDb(taps.far);
            const mic = rmsDb(taps.mic);
            const pre = rmsDb(taps.pre);
            const post = rmsDb(taps.post);
            const tx = rmsDb(taps.tx);
            const gain = (this._agcEnabled && this._makeupGainNode)
                ? this._makeupGainNode.gain.value : 1;
            // far鳴っている＋micも鳴っている＝エコー/ダブルトーク区間の目印。
            const farHot = far != null && far > -50;
            const pk = {
                far: peakHz(taps.far), mic: peakHz(taps.mic), pre: peakHz(taps.pre),
                post: peakHz(taps.post), tx: peakHz(taps.tx)
            };

            this._meter.last = { far, mic, pre, post, tx, gain, pk };

            // 入口と出口で主成分の周波数が変わっていれば、変えたのは途中の処理。
            // 1割以上ずれたときだけ言う(揺らぎで毎回出ると読めない)。
            const moved = pk.mic && pk.tx
                && Math.abs(pk.tx.hz - pk.mic.hz) > pk.mic.hz * 0.1;

            const fHz = p => (p == null ? '&nbsp;&nbsp;&nbsp;&nbsp;--&nbsp;&nbsp;&nbsp;'
                : `${p.hz >= 1000 ? (p.hz / 1000).toFixed(2) + 'k' : p.hz.toFixed(0)}Hz`.padStart(8));

            // 画面は読み取り専用で、状況を人に渡すにはコピーが要る。ここまでの
            // やり取りは全部コンソールのログを貼る形で進んでいるので、同じ口に
            // 出す。2秒ごと(タイマーは200ms)。
            this._specTick = (this._specTick || 0) + 1;
            if (this._specTick % 10 === 0) {
                const one = (name, db, p) => `${name}=${db == null ? '--' : db.toFixed(0) + 'dB'}`
                    + `/${p == null ? '--' : (p.hz >= 1000 ? (p.hz / 1000).toFixed(2) + 'kHz' : p.hz.toFixed(0) + 'Hz')}`;

                // 判定は入口(mic)と出口(tx)の主成分がずれたかどうかだけで決まる。
                // 音程のある雑音は「鳴っているか」ではなく「途中で周波数が変わるか」
                // でしか犯人を特定できない。
                let verdict;

                if (!pk.mic && pk.tx) {
                    verdict = '★入口に無い成分が出口にある → AEC が作っています';
                } else if (moved) {
                    const src = 16000 - pk.tx.hz;

                    verdict = `★周波数が移動 → 折り返しです`
                        + `（出口${(pk.tx.hz / 1000).toFixed(2)}kHz の折り返し元は`
                        + ` ${(src / 1000).toFixed(2)}kHz 付近）`;
                } else if (pk.mic && pk.tx) {
                    verdict = '入口と出口で同じ → 処理は無実。部屋かマイク由来';
                } else {
                    verdict = '判定不能（音が小さい。少し喋ってください）';
                }

                console.info(`[nkf-spec] ${one('mic', mic, pk.mic)}  ${one('AEC後', pre, pk.pre)}`
                    + `  ${one('送信', tx, pk.tx)}  ${one('far', far, pk.far)}`
                    + `  ゲイン×${gain.toFixed(1)}\n           ${verdict}`);
            }

            el.innerHTML =
                `<div style="font-weight:700;color:${farHot ? '#ffb454' : '#7f8aa3'};margin-bottom:4px">`
                + `NKF送信メーター ${farHot ? '（相手発話中）' : ''}</div>`
                + `<div>far(相手) : ${fmt(far)} ${fHz(pk.far)}</div>`
                + `<div>mic(入力) : ${fmt(mic)} ${fHz(pk.mic)}</div>`
                + `<div>NKF出力 &nbsp;: ${fmt(pre)} ${fHz(pk.pre)}</div>`
                + `<div>ゲイン後 &nbsp;: ${fmt(post)} ${fHz(pk.post)} (×${gain.toFixed(1)})</div>`
                + `<div style="color:#ff9aa2">送信 &nbsp;&nbsp;&nbsp;: ${fmt(tx)} ${fHz(pk.tx)}</div>`
                + (moved
                    ? `<div style="margin-top:5px;padding:4px 6px;border-radius:6px;`
                      + `background:rgba(255,120,120,.15);color:#ff9aa2">`
                      + `主成分が ${fHz(pk.mic).trim()} → ${fHz(pk.tx).trim()} に移動`
                      + `<br>途中の処理が作っています</div>`
                    : '');
        }, 200);

        this._meter = { timer, el, taps };
        console.info('[nkf] transmit meter ON (?nkfMeter=1)');
    }

    /**
     * 送信メーターを実行時に開閉する。
     *
     * エフェクトが作り直されても指定が残るよう、希望の状態を持っておく
     * (_meterWanted)。グラフを組み直すたびに消えると、切り分けの途中で
     * 見失う。
     *
     * @param {boolean} on - true で表示。
     * @returns {void}
     */
    setTransmitMeter(on) {
        this._meterWanted = on === true;
        if (this._meterWanted) {
            this._maybeStartTransmitMeter();
        } else {
            this._stopTransmitMeter();
        }
    }

    /** Tear down the transmit meter if running. */
    /**
     * 音量の自動調整(メイクアップゲイン)を実行時に入切する。
     *
     * OFF にしたときはゲインを 1 倍へ戻す。止めるだけだと、直前に持ち上がっていた
     * 倍率がそのまま固定されて「OFF にしたのに大きいまま」になる。
     *
     * リミッターは触らない。あれは -1.5 dBFS で頭を押さえるだけの安全装置で、
     * 自動調整を切りたい人が求めているのは音量が勝手に動くことの停止であって、
     * 歪んでよいという意味ではない。
     *
     * @param {boolean} enabled - true で自動調整を有効化。
     * @returns {void}
     */
    setMakeupGain(enabled) {
        this._agcActive = enabled !== false;
        console.info(`[nkf] 音量の自動調整 ${this._agcActive ? 'ON' : 'OFF'}`);

        if (!this._agcActive && this._makeupGainNode && this._ac) {
            try {
                this._makeupGainNode.gain.setTargetAtTime(1, this._ac.currentTime, 0.1);
            } catch { /* ignore */ }
            // 平滑値も捨てる。残しておくと再開時に古い音量へ一気に飛ぶ。
            this._agcLevel = 0;
        }
    }

    _stopTransmitMeter() {
        if (!this._meter) return;
        try { clearInterval(this._meter.timer); } catch { /* ignore */ }
        for (const a of Object.values(this._meter.taps)) {
            try { a?.disconnect(); } catch { /* ignore */ }
        }
        try { this._meter.el?.remove(); } catch { /* ignore */ }
        this._meter = null;
    }

    /**
     * Lazily loads the RNNoise worklet module into the AEC AudioContext and
     * builds the suppressor node. The context runs at the device rate (48 kHz
     * via RemoteFarEndMix), which is exactly what rnnoise expects.
     *
     * @returns {Promise<void>}
     */
    async _ensureNsNode() {
        if (this._nsNode || !this._ac) {
            return;
        }
        if (!this._nsModulePromise) {
            this._nsModulePromise = this._ac.audioWorklet.addModule(this._nsWorkletPath);
        }
        await this._nsModulePromise;
        if (!this._nsNode && this._ac) {
            this._nsNode = new AudioWorkletNode(this._ac, 'NoiseSuppressorWorklet');
        }
    }

    /**
     * Engages or bypasses the RNNoise denoise stage live, without tearing down
     * the AEC graph. The choice survives effect (re)creation via the pending
     * flag in createNkfAecEffect.
     *
     * @param {boolean} enabled - True to denoise, false to bypass (raw AEC out).
     * @returns {void}
     */
    setNoiseSuppression(enabled) {
        this._nsEnabled = Boolean(enabled);
        console.info('[nkf] RNNoise ' + (this._nsEnabled ? 'ON' : 'OFF'));
        if (!this._ac || !this._emitNode) {
            return; // graph not up yet; applied at startEffect
        }
        if (this._nsEnabled) {
            this._ensureNsNode()
                .then(() => this._wireOutputChain())
                .catch(e => console.error('[nkf] NS enable err:', e));
        } else {
            this._wireOutputChain();
        }
    }

    stopEffect() {
        console.info('[nkf] stopEffect — disengaging AEC (graph torn down, engine kept warm)');
        console.info(`[nkf-dbg] effect#${this._effectId} 停止 受信登録数=${_sharedWorkerEffects.size}→${_sharedWorkerEffects.size - 1}`);

        if (_activeEffect === this) {
            _activeEffect = null;
        }
        if (typeof document !== 'undefined') {
            window.__nkfAecRunning = false;
            document.dispatchEvent(new CustomEvent('nkf-aec-stopped'));
        }
        // Stop passive delay tracking for this run (recreated on next start).
        try { this._delayTracker?.stop(); } catch { /* ignore */ }
        this._delayTracker = null;
        if (this._outLatPollId) { clearInterval(this._outLatPollId); this._outLatPollId = null; }

        // Tear down the diagnostic transmit meter if it was running.
        this._stopTransmitMeter();

        // Worker stays alive (warm) for the next startEffect; just clear the
        // in-flight counter so stale results don't wedge the backpressure gate.
        this._workerPending = 0;
        clearTimeout(this._readyTimer);
        clearTimeout(this._txHoldTimer);
        this._txHold = false;
        // Stop receiving: our graph is about to be torn down, so results still
        // in flight have nowhere to go. startEffect() registers us again.
        _sharedWorkerEffects.delete(this._effectId);
        // Tear down the per-startEffect graph but keep _ac, engine and
        // _loaded alive so a subsequent setEffect() can re-attach without
        // paying for model load + addModule again.
        try { this._workletNode?.port.close(); } catch {}
        try { this._workletNode?.disconnect(); } catch {}
        try {
            // Disconnect the far-end node from input 1 specifically (don't
            // tear down the caller's mix bus — they still own that node).
            this.farEndSource?.disconnect(this._workletNode);
        } catch {}
        try { this._emitNode?.disconnect(); } catch {}
        // Dispose the RNNoise node but keep _nsModulePromise so a warm restart
        // skips addModule(); a fresh node is built on the next startEffect.
        try { this._nsNode?.port?.close(); } catch {}
        try { this._nsNode?.disconnect(); } catch {}
        try { this._makeupGainNode?.disconnect(); } catch {}
        try { this._limiterNode?.disconnect(); } catch {}
        try { this._destination?.disconnect(); } catch {}
        try { this._micSource?.disconnect(); } catch {}
        this._micSource = this._farSource = this._workletNode = null;
        this._emitNode = this._destination = null;
        this._makeupGainNode = this._limiterNode = null;
        this._nsNode = null;
        this._micRem = new Int16Array(0);
        this._farRem = new Int16Array(0);
        this._aecQueue = Promise.resolve();
        // Don't suspend _ac: we don't own it (RemoteFarEndMix does), and
        // suspending would also stop the remote-mix bus.
    }
}

// ── visual debug overlay ────────────────────────────────────────────────────
// Three rolling waveform canvases (mic / far / cleaned), with a 🐛 toggle
// in the top-right.  The toggle persists across reloads via localStorage so
// engineers can leave instrumentation in place without burdening end users.
const _NKF_DEBUG_BUF_SECONDS = 4;
const _NKF_DEBUG_BUF_LEN     = 16000 * _NKF_DEBUG_BUF_SECONDS;
const _NKF_DEBUG_LS_KEY      = 'nkf-debug-visible';

let _nkfDebugCtx = null;          // { mic, far, cleaned } 2d contexts
let _nkfDebugBuf = null;          // { mic, far, cleaned } Float32Array ring
let _nkfDebugWrap = null;         // overlay <div>
let _nkfDebugVisible = false;
let _nkfDebugRafPending = false;
let _nkfWavePhase = 0;            // animation phase for the ribbon waveform

// ── on-screen log panel ─────────────────────────────────────────────────────
// Devices without console access (tablets, kiosk browsers) need the [nkf]
// diagnostics visible in-page.  console.info/warn/error are wrapped ONCE at
// module load; any call whose args mention nkf is copied into a ring buffer
// rendered in a panel toggled by the LOG button next to the waveform toggle.
const _NKF_LOG_MAX = 300;
const _nkfLogBuf = [];            // { t:'HH:MM:SS', level, line }
let _nkfLogPanel = null;
let _nkfLogVisible = false;

(function _nkfLogCapture() {
    if (typeof console === 'undefined' || typeof document === 'undefined') return;
    for (const level of [ 'info', 'warn', 'error' ]) {
        const orig = console[level].bind(console);
        console[level] = (...args) => {
            try {
                const hit = args.some(a => typeof a === 'string'
                    && (a.includes('[nkf') || a.includes('nkf-') || a.includes('NKF')));
                if (hit) {
                    const line = args.map(a => {
                        if (typeof a === 'string') return a;
                        try { return JSON.stringify(a); } catch { return String(a); }
                    }).join(' ');
                    _nkfLogBuf.push({ t: new Date().toTimeString().slice(0, 8), level, line });
                    if (_nkfLogBuf.length > _NKF_LOG_MAX) _nkfLogBuf.shift();
                    if (_nkfLogVisible) _nkfLogRender();
                }
            } catch { /* never break console */ }
            orig(...args);
        };
    }
})();

function _nkfLogRender() {
    if (!_nkfLogPanel) return;
    const colors = { info: '#bbb', warn: '#fd6', error: '#f77' };
    _nkfLogPanel.innerHTML = _nkfLogBuf.map(e =>
        `<div style="color:${colors[e.level] || '#bbb'};white-space:pre-wrap;word-break:break-all">`
        + `${e.t} ${e.line}</div>`).join('')
        || '<div style="color:#888">（まだログはありません）</div>';
    _nkfLogPanel.scrollTop = _nkfLogPanel.scrollHeight;
}

function _nkfDebugInit() {
    if (typeof document === 'undefined') return;
    // Always default to OFF on page load — engineers reach for the toggle
    // when they need it.  We do NOT persist to localStorage so a stale
    // visible flag from a prior session can't surprise end users.
    _nkfDebugVisible = false;

    // NOTE: The floating top-left waveform toggle and LOG toggle buttons were
    // removed — the toolbar now owns those actions (「スピーカー」= overlay,
    // 「ログ」in 詳細な操作 = log panel), driven via the exported
    // setNkfDebugOverlayVisible / setNkfLogVisible below.

    // Log panel — bottom-left so it never collides with the waveform overlay.
    const logPanel = document.createElement('div');
    logPanel.id = 'nkf-log-panel';
    logPanel.style.cssText =
        'position:fixed;left:12px;bottom:12px;z-index:99999;'
        + 'width:min(480px,90vw);max-height:40vh;overflow-y:auto;'
        + 'background:rgba(0,0,0,.85);color:#bbb;'
        + 'font:10px/1.5 ui-monospace,monospace;'
        + 'padding:8px;border-radius:6px;display:none;'
        + 'pointer-events:auto;user-select:text;-webkit-overflow-scrolling:touch;';
    document.body.appendChild(logPanel);
    _nkfLogPanel = logPanel;

    // Overlay (always built; visibility toggled).
    const wrap = document.createElement('div');
    wrap.id = 'nkf-debug-overlay';
    wrap.style.cssText =
        'position:fixed;left:12px;top:54px;z-index:99999;width:300px;'
        + 'padding:12px 14px;background:rgba(16,18,24,.82);'
        + 'backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);'
        + 'border:1px solid rgba(255,255,255,.08);border-radius:14px;'
        + 'box-shadow:0 8px 28px rgba(0,0,0,.45);color:#e6e9ef;'
        + 'font:11px/1.4 system-ui,sans-serif;pointer-events:none;user-select:none;display:none;';
    const _wave = (id, name, sub, col) =>
        `<div style="display:flex;align-items:center;gap:6px;margin:9px 0 3px">`
        + `<span style="width:7px;height:7px;border-radius:50%;background:${col};box-shadow:0 0 7px ${col}"></span>`
        + `<span style="font:700 10px/1 system-ui;letter-spacing:.04em;color:#cdd2dd">${name}</span>`
        + `<span style="font:10px/1 system-ui;color:#7d8598">${sub}</span></div>`
        + `<canvas id="${id}" width="560" height="140" style="width:100%;height:70px;display:block;border-radius:10px;background:#070d1e"></canvas>`;
    wrap.innerHTML =
        '<button id="nkf-debug-close" type="button" aria-label="波形を閉じる"'
        + ' style="position:absolute;right:8px;top:8px;width:28px;height:28px;'
        + 'border:none;border-radius:8px;background:rgba(255,255,255,.12);color:#e6e9ef;'
        + "font:700 17px/1 system-ui;cursor:pointer;pointer-events:auto;"
        + 'display:grid;place-items:center'
        + '">×</button>'
        + '<div style="font:700 10px/1.4 system-ui;letter-spacing:.09em;color:#8b93a7;margin-bottom:8px">NKF-AEC MONITOR</div>'
        + '<div id="nkf-cal-info" style="margin-bottom:5px;padding:5px 9px;background:rgba(255,255,255,.05);border-radius:9px;font:11px/1.5 ui-monospace,monospace;color:#c7ccd8">キャリブレーション: —</div>'
        + '<div id="nkf-effect-state" style="padding:5px 9px;background:rgba(120,170,255,.13);border-radius:9px;font:11px/1.5 ui-monospace,monospace">エフェクト適用値: 未設定</div>'
        + _wave('nkf-cv-mic', 'MIC', '送信前', '#ffcf6b')
        + _wave('nkf-cv-far', 'FAR', 'AI / 受信', '#4cd4e6')
        + _wave('nkf-cv-cleaned', 'CLEANED', '送信', '#5be08c');
    document.body.appendChild(wrap);
    // Close button (the overlay itself is pointer-events:none; the button
    // re-enables pointer events so it can be tapped on mobile, where the toolbar
    // toggle isn't reachable while the overlay is up).
    const _closeBtn = wrap.querySelector('#nkf-debug-close');
    if (_closeBtn) {
        _closeBtn.addEventListener('click', () => setNkfDebugOverlayVisible(false));
    }
    _nkfDebugWrap = wrap;

    _nkfDebugCtx = {
        mic:     wrap.querySelector('#nkf-cv-mic').getContext('2d'),
        far:     wrap.querySelector('#nkf-cv-far').getContext('2d'),
        cleaned: wrap.querySelector('#nkf-cv-cleaned').getContext('2d')
    };
    _nkfDebugBuf = {
        mic:     new Float32Array(_NKF_DEBUG_BUF_LEN),
        far:     new Float32Array(_NKF_DEBUG_BUF_LEN),
        cleaned: new Float32Array(_NKF_DEBUG_BUF_LEN)
    };
}

function _nkfDebugPush(label, samplesI16, scale) {
    if (typeof document === 'undefined') return;
    if (!_nkfDebugCtx) _nkfDebugInit();
    const buf = _nkfDebugBuf[label];
    if (!buf) return;
    const g = (typeof scale === 'number' && scale > 0) ? scale : 1;   // e.g. makeup gain → show post-amplification
    const norm = g / 32768;
    const n = samplesI16.length;
    if (n >= buf.length) {
        // New chunk longer than the ring — keep only the tail.
        const off = n - buf.length;
        for (let i = 0; i < buf.length; i++) buf[i] = samplesI16[off + i] * norm;
    } else {
        buf.copyWithin(0, n);
        const tail = buf.length - n;
        for (let i = 0; i < n; i++) buf[tail + i] = samplesI16[i] * norm;
    }

    if (!_nkfDebugVisible) return;
    if (_nkfDebugRafPending) return;
    _nkfDebugRafPending = true;
    requestAnimationFrame(() => {
        _nkfDebugRafPending = false;
        _nkfWavePhase += 0.05;
        _nkfDebugRedraw('mic',     u => `rgba(255,${180 + ((50 * u) | 0)},90,0.15)`, 16);
        _nkfDebugRedraw('far',     u => `rgba(70,${200 + ((40 * u) | 0)},240,0.15)`, 10);
        // cleaned buffer is already scaled by the makeup gain → use a low display gain
        _nkfDebugRedraw('cleaned', u => `rgba(90,${210 + ((30 * u) | 0)},150,0.15)`, 1.5);
    });
}

// Visual gain so typical speech at ~-20 dBFS (= ±0.1) fills most of the
// canvas height instead of being a barely visible squiggle.  Loud signals
// are clamped to the canvas edges (visually distorts but flags clipping).
const _NKF_DEBUG_VISUAL_GAIN = 6;
// Ribbon waveform is thin lines, so it needs more visual gain than the old
// filled envelope to read as a wave on very quiet mics (~-48 dBFS here).
const _NKF_DEBUG_RIBBON_GAIN = 16;

// coestation-style "string" waveform: a bundle of phase-shifted sine lines
// whose amplitude follows the real audio envelope, additively blended for glow.
function _nkfDebugRedraw(label, colorFn, gain) {
    const ctx = _nkfDebugCtx?.[label];
    const buf = _nkfDebugBuf?.[label];
    if (!ctx || !buf) return;
    const w = ctx.canvas.width, h = ctx.canvas.height, half = h / 2;

    // navy background
    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#0a1a38'); bg.addColorStop(1, '#070d1e');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);

    // per-column amplitude envelope from the real audio buffer (0..1)
    const spc = buf.length / w;
    const env = new Float32Array(w);
    for (let x = 0; x < w; x++) {
        const s = Math.floor(x * spc), e = Math.floor((x + 1) * spc);
        let mx = 0;
        for (let i = s; i < e; i++) { const v = buf[i] < 0 ? -buf[i] : buf[i]; if (v > mx) mx = v; }
        env[x] = Math.min(1, mx * (gain || _NKF_DEBUG_RIBBON_GAIN));
    }
    // light smoothing → flowing ribbon
    for (let p = 0; p < 2; p++) {
        let prev = env[0];
        for (let x = 1; x < w - 1; x++) { const cur = env[x]; env[x] = (prev + cur + env[x + 1]) / 3; prev = cur; }
    }

    const N = 26, cycles = 2.4, amp = half - 2;
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineWidth = 1;
    for (let i = 0; i < N; i++) {
        const u = i / (N - 1);
        const ph = _nkfWavePhase * 0.9 + u * Math.PI * 1.7;
        const aLine = 0.30 + 0.70 * Math.sin(u * Math.PI);   // fuller bundle in the middle
        ctx.beginPath();
        for (let x = 0; x <= w; x += 2) {
            const xn = x / w;
            const win = Math.pow(Math.sin(Math.PI * xn), 0.7);   // taper to a line at both ends
            const ex = env[x < w ? x : w - 1];                   // real audio envelope (flat when silent)
            const y = half - Math.sin(2 * Math.PI * cycles * xn + ph) * win * ex * aLine * amp;
            x ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.strokeStyle = colorFn(u);
        ctx.stroke();
    }
    ctx.globalCompositeOperation = 'source-over';
}

// Inline worklet that resamples 16 kHz Int16 → AC sr float, OLA-buffered.
const EMIT_WORKLET_SRC = `
// Upper bound on the emit jitter buffer (in 512-sample / 32 ms chunks).
// Producer (sync-aec → worker → cleaned) and consumer (this playout) run at
// the same AudioContext rate, so whatever backlog builds at startup never
// drains — it becomes permanent added latency on the transmitted mic. On
// mobile/iOS the startup backlog is huge (100+ chunks ≈ 400 ms–1 s+),
// causing the "sent audio is delayed" symptom. Trimming the queue to the
// newest EMIT_QUEUE_CAP chunks bounds the added latency to cap*32 ms.
// Measured: iPhoneX 430→139 ms, Android 547→133 ms; drops occur only as a
// one-time startup trim (post-startup drop rate ≈ 0, no ongoing choppiness).
const EMIT_QUEUE_CAP = 3;
class NkfEmit extends AudioWorkletProcessor {
    constructor() {
        super();
        this.queue = [];
        this.phase = 0;
        this.ratio = 16000 / sampleRate;
        // Dropout (underrun) instrumentation: when the queue is empty we emit
        // silence into the TRANSMITTED stream — a real hole the far side hears
        // as 音の途切れ. Count the zero-filled samples and the number of
        // distinct gaps, and report them to the main thread ~4x/sec so the UI
        // can prove the dropout live. Startup silence (before the first cleaned
        // chunk arrives) is excluded via everHadData.
        this.everHadData = false;
        this.urSamples = 0;       // zero-filled OUTPUT samples since last report
        this.urEvents = 0;        // distinct empty-queue gaps since last report
        this.inGap = false;
        this.trimSamples16k = 0;  // dropped cleaned samples (16 kHz) since last report
        this.trimEvents = 0;      // overflow-trim bursts since last report
        this.sinceReport = 0;     // output samples since last report
        // Queue depth. The counters above only fire once the queue has already
        // run dry or overflowed — by then the audio is gone. Depth says how
        // close we are BEFORE that happens: sitting at 0-1 means any hiccup
        // punches a hole, while 2-3 means there is room to absorb one.
        this.qMin = 99;
        this.qSum = 0;
        this.qN = 0;
        this.port.onmessage = (e) => {
            this.queue.push(e.data);
            this.everHadData = true;
            if (this.queue.length > EMIT_QUEUE_CAP) {
                // Overflow trim: cleaned audio is arriving faster than we play
                // it out — the classic signature of the render thread having
                // been FROZEN (CPU) so port messages piled up and now flush in
                // a burst. Dropping them is a REAL hole in the transmitted
                // audio that the empty-queue counter never sees (the queue is
                // non-empty right after the burst). Count it as a dropout.
                const drop = this.queue.length - EMIT_QUEUE_CAP;
                for (let k = 0; k < drop; k++) {
                    const c = this.queue[k];
                    if (c && c.length) this.trimSamples16k += c.length;
                }
                this.trimEvents += 1;
                this.queue.splice(0, drop);
            }
        };
    }
    process(_inputs, outputs) {
        const out = outputs[0][0];
        if (!out) return true;
        for (let i = 0; i < out.length; i++) {
            if (!this.queue.length) {
                out[i] = 0;
                if (this.everHadData) {
                    this.urSamples++;
                    if (!this.inGap) { this.urEvents++; this.inGap = true; }
                }
                continue;
            }
            this.inGap = false;
            const cur = this.queue[0];
            const i0 = Math.floor(this.phase);
            const frac = this.phase - i0;
            const a = (cur[i0] || 0) / 32768;
            const b = (cur[i0+1] !== undefined ? cur[i0+1] : (cur[i0] || 0)) / 32768;
            out[i] = a + (b - a) * frac;
            this.phase += this.ratio;
            if (this.phase >= cur.length) { this.phase -= cur.length; this.queue.shift(); }
        }
        const _q = this.queue.length;

        if (_q < this.qMin) this.qMin = _q;
        this.qSum += _q;
        this.qN++;
        this.sinceReport += out.length;
        if (this.sinceReport >= (sampleRate >> 2)) {   // ~every 250 ms
            this.port.postMessage({
                __underrun: true,
                urMs: this.urSamples / sampleRate * 1000,
                urEvents: this.urEvents,
                trimMs: this.trimSamples16k / 16000 * 1000,
                trimEvents: this.trimEvents,
                qMin: this.qMin === 99 ? 0 : this.qMin,
                qAvg: this.qN ? this.qSum / this.qN : 0,
                qCap: EMIT_QUEUE_CAP
            });
            this.urSamples = 0;
            this.urEvents = 0;
            this.trimSamples16k = 0;
            this.trimEvents = 0;
            this.qMin = 99;
            this.qSum = 0;
            this.qN = 0;
            this.sinceReport = 0;
        }
        return true;
    }
}
// ★ AudioWorkletGlobalScope 内。ここから外側の識別子は一切見えない。
//
// この module は同じ AudioContext で二度以上評価されうる。emit ワークレットは
// 毎回あたらしい blob URL から addModule されるので、ブラウザの module map では
// 重複を潰せないため。二度目の registerProcessor は
//   NotSupportedError: ... name:"nkf-emit" is already registered
// を投げ、module の評価ごと失敗させる。すると addModule が reject し、load() が
// 落ち、エフェクトが組み上がらない ＝ マイクが無音になる。
// （マイクOFFで入室してから有効化する = 2個目のエフェクト生成、で必ず踏む。）
//
// 一度登録できていれば以後の登録は不要であり、二度目が失敗すること自体は正常。
// 名前の重複だけを飲み込み、それ以外の失敗は握り潰さずに投げる。
try {
    registerProcessor('nkf-emit', NkfEmit);
} catch (e) {
    if (!/already registered/i.test(String((e && e.message) || e))) throw e;
}
`;

// Always-visible dropout badge: proves 音の途切れ (emit underrun = a real hole
// in the transmitted audio) live, without a debugger. Stays on-screen once any
// dropout is seen so it can be screenshotted as evidence; flashes red on each
// fresh gap. Top-right so it doesn't collide with the delay badge / overload card.
let _nkfUnderrunEl = null;
let _nkfUnderrunFlashTimer = null;
function _nkfSetUnderrunBadge(totalEvents, totalMs, flash) {
    if (typeof document === 'undefined') return;
    // On-screen badge disabled: dropout counts still go to the alive log
    // (途切れ=…). Remove any badge left over from a previous build.
    if (_nkfUnderrunEl) { try { _nkfUnderrunEl.remove(); } catch { /* ignore */ } _nkfUnderrunEl = null; }
    return;
    // eslint-disable-next-line no-unreachable
    if ((totalEvents | 0) <= 0) return;   // nothing to show yet
    if (!_nkfUnderrunEl) {
        const el = document.createElement('div');
        el.id = 'nkf-underrun-badge';
        el.style.cssText =
            'position:fixed;right:12px;top:12px;z-index:100000;'
            + 'display:flex;align-items:center;gap:8px;padding:8px 12px;'
            + 'background:linear-gradient(135deg,#3a1c1c,#2a1622);'
            + 'border:1px solid rgba(231,76,60,.55);border-radius:10px;'
            + 'box-shadow:0 8px 24px rgba(0,0,0,.45);color:#ffd9d3;'
            + 'font:12px/1.35 system-ui,sans-serif;pointer-events:none;'
            + 'transition:background .15s;';
        document.body.appendChild(el);
        _nkfUnderrunEl = el;
    }
    _nkfUnderrunEl.innerHTML =
        '<span style="font-size:16px;line-height:1">🔇</span>'
        + '<div><div style="font-weight:700;color:#ff8a7a">音の途切れを検知</div>'
        + `<div style="opacity:.9;font-size:11px;margin-top:1px">累計 <b>${totalEvents | 0}</b> 回 / <b>${Math.round(totalMs)}</b> ms（送信音声の欠落）</div></div>`;
    if (flash) {
        _nkfUnderrunEl.style.background = 'linear-gradient(135deg,#7a2e2e,#5a2436)';
        if (_nkfUnderrunFlashTimer) clearTimeout(_nkfUnderrunFlashTimer);
        _nkfUnderrunFlashTimer = setTimeout(() => {
            if (_nkfUnderrunEl) _nkfUnderrunEl.style.background = 'linear-gradient(135deg,#3a1c1c,#2a1622)';
        }, 180);
    }
}

// User-facing overload notification card (this device can't run the AEC in
// real-time → audio glitches / echo leaks). Auto-shows/hides.
let _nkfOverloadEl = null;
function _nkfSetOverload(on, detail) { // eslint-disable-line no-unused-vars
    if (typeof document === 'undefined') return;
    // On-screen overload card disabled by request. Overload is still visible in
    // the alive log (bypassed=N). Remove any card left over from a prior build.
    if (_nkfOverloadEl) {
        try { _nkfOverloadEl.remove(); } catch { /* ignore */ }
        _nkfOverloadEl = null;
    }
}

function _updateEffectStateDebug(farDelaySamples, engineReady, erleDb) {
    if (typeof document === 'undefined') return;
    if (!_nkfDebugWrap) _nkfDebugInit();
    const el = document.getElementById('nkf-effect-state');
    if (!el) return;
    const delayMs = farDelaySamples * 1000 / 16000;
    const engineLabel = engineReady ? '✓ engine有' : '✗ engine無';
    const erleLabel = (typeof erleDb === 'number')
        ? ` &nbsp; ERLE: <b>${erleDb.toFixed(1)} dB</b>` : '';
    el.innerHTML = `エフェクト適用値: <b>${farDelaySamples} samples (${delayMs.toFixed(1)} ms)</b> ${engineLabel}${erleLabel}`;
    el.style.color = farDelaySamples > 0 ? '#cfc' : '#f88';
}


/**
 * Shows or hides the NKF-AEC debug waveform overlay (the same overlay the
 * floating top-left waveform button toggles). Exposed so a toolbar button can
 * drive it from React.
 * @param {boolean} visible
 */
export function setNkfDebugOverlayVisible(visible) {
    if (typeof document === 'undefined') return;
    if (!_nkfDebugWrap) _nkfDebugInit();
    _nkfDebugVisible = Boolean(visible);
    if (_nkfDebugWrap) _nkfDebugWrap.style.display = _nkfDebugVisible ? 'block' : 'none';
}

/**
 * Returns whether the NKF-AEC debug waveform overlay is currently visible.
 * @returns {boolean}
 */
export function isNkfDebugOverlayVisible() {
    return _nkfDebugVisible;
}

/**
 * Shows or hides the NKF-AEC debug LOG panel (moved from the floating top-left
 * LOG button into the toolbar's 詳細な操作 menu).
 * @param {boolean} visible
 */
export function setNkfLogVisible(visible) {
    if (typeof document === 'undefined') return;
    if (!_nkfDebugWrap) _nkfDebugInit();
    _nkfLogVisible = Boolean(visible);
    if (_nkfLogPanel) {
        _nkfLogPanel.style.display = _nkfLogVisible ? 'block' : 'none';
        if (_nkfLogVisible) _nkfLogRender();
    }
}

/**
 * Returns whether the NKF-AEC debug LOG panel is currently visible.
 * @returns {boolean}
 */
export function isNkfLogVisible() {
    return _nkfLogVisible;
}

/**
 * Update the calibration info displayed in the waveform debug overlay.
 * @param {number} delaySamples
 * @param {number} delayMs
 * @param {number|null} peakFloorRatio
 * @param {'prejoin'|'insitu'} source
 */
export function setCalibrationDebugInfo(delaySamples, delayMs, peakFloorRatio, source) {
    if (typeof document === 'undefined') return;
    if (!_nkfDebugWrap) _nkfDebugInit();

    // Detail inside the waveform overlay.
    const el = document.getElementById('nkf-cal-info');
    if (!el) return;
    const snrLabel = peakFloorRatio != null
        ? ` / SNR ${peakFloorRatio.toFixed(1)}`
        : '';
    const sourceLabel = source === 'prejoin' ? '待機画面' : '入室後';
    const quality = peakFloorRatio == null ? ''
        : peakFloorRatio < 5  ? ' ⚠ 低信頼'
        : peakFloorRatio < 20 ? ' △ 中信頼'
        : ' ✓ 高信頼';
    el.innerHTML = `キャリブレーション (${sourceLabel})<br>`
        + `遅延: <b>${delaySamples} samples (${delayMs.toFixed(1)} ms)</b>${snrLabel}${quality}`;
    el.style.color = peakFloorRatio != null && peakFloorRatio < 5 ? '#f88' : '#cfc';
}
